/**
 * Rubicon components: slideshow, carousel rows, tabs, quick add.
 * Loaded once as a module from snippets/scripts.liquid.
 * Quick add reuses Horizon's cart events so the cart icon and drawer update normally.
 */
import { CartDiscountUpdateEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// Runs fn once the page has loaded and the browser is idle (at once in the theme editor). Autoplay
// (slideshows, the announcement ticker) waits for this, so nothing moves on screen while the page is still
// loading: page speed tests count those changes as unfinished loading (Speed Index).
const afterLoad = (fn) => {
  if (window.Shopify?.designMode) { fn(); return; }
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
  if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
};
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------------- Carousel rows ---------------- */
class RgsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.rgs-carousel__track');
    this.prev = this.querySelector('[data-rgs-prev]');
    this.next = this.querySelector('[data-rgs-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.onScroll = () => this.update();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    // The ResizeObserver reports the track's first size right after layout, which runs update() then.
    // (Calling update() here as well forced a full-page layout while the page was still loading.)
    this.ro = new ResizeObserver(() => this.update());
    this.ro.observe(this.track);
  }
  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.ro?.disconnect();
  }
  go(dir) {
    const item = this.track.firstElementChild;
    if (!item) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.round(this.track.clientWidth / step));
    this.track.scrollBy({ left: dir * step * perView, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    this.toggleAttribute('data-static', max <= 0);
  }
}
if (!customElements.get('rgs-carousel')) customElements.define('rgs-carousel', RgsCarousel);

/* ---------------- Slideshow (sections/rgs-slideshow, and rgs-hero with Slide blocks) ----------------
   Optional extras the markup can opt into: [data-rgs-live] (aria-live region, "off" while rotating,
   "polite" when stopped) and [data-rgs-toggle] (pause/play button). Autoplay pauses on hover, focus,
   a hidden tab and while a slide is selected in the theme editor, and never runs with reduced motion. */
class RgsSlideshow extends HTMLElement {
  // Parts that belong to this slideshow, not to one nested inside it (a hero with Slide blocks
  // inside a rotating hero group).
  own(sel) {
    return [...this.querySelectorAll(sel)].filter((el) => el.closest('rgs-slideshow, rgs-hero-group') === this);
  }
  ownEvent(e) {
    return !(e.target instanceof Element) || e.target.closest('rgs-slideshow, rgs-hero-group') === this;
  }
  connectedCallback() {
    this.slides = this.own('.rgs-slide');
    this.dots = this.own('[data-rgs-dot]');
    this.live = this.own('[data-rgs-live]')[0] || null;
    this.toggle = this.own('[data-rgs-toggle]')[0] || null;
    this.index = 0;
    if (this.slides.length < 2) return;
    this.own('[data-rgs-prev]')[0]?.addEventListener('click', () => { this.show(this.index - 1); this.restart(); });
    this.own('[data-rgs-next]')[0]?.addEventListener('click', () => { this.show(this.index + 1); this.restart(); });
    this.dots.forEach((d, i) => d.addEventListener('click', () => { this.show(i); this.restart(); }));
    this.addEventListener('keydown', (e) => {
      if (!this.ownEvent(e)) return;
      if (e.key === 'ArrowLeft') { this.show(this.index - 1); this.restart(); }
      if (e.key === 'ArrowRight') { this.show(this.index + 1); this.restart(); }
    });
    // Swipe
    let x0 = null;
    this.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' && this.ownEvent(e)) x0 = e.clientX; });
    this.addEventListener('pointercancel', () => { x0 = null; });
    this.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) { this.show(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
    });
    // Pause on hover or focus
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.restart());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.restart(); });
    // Pause while the tab is hidden
    this.onVisibility = () => (document.hidden ? this.stop() : this.restart());
    document.addEventListener('visibilitychange', this.onVisibility);
    // Pause/play button
    if (this.toggle) {
      const canPlay = Number(this.dataset.autoplay || 0) > 0 && !reduceMotion.matches;
      this.toggle.hidden = !canPlay;
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.userPaused) this.stop(); else this.start();
        this.syncToggle();
      });
      this.syncToggle();
    }
    // Theme editor: jump to the selected slide and hold it until it is deselected
    this.onBlockSelect = (e) => {
      const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
      if (i >= 0) { this.held = true; this.stop(); this.show(i); }
    };
    this.onBlockDeselect = (e) => {
      if (!this.held || !this.slides.some((s) => s.contains(e.target) || s === e.target)) return;
      this.held = false;
      this.restart();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.show(0);
    afterLoad(() => { this.ready = true; this.restart(); });
  }
  disconnectedCallback() {
    this.stop();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }
  show(i) {
    const n = this.slides.length;
    this.index = (i + n) % n;
    this.slides.forEach((s, j) => {
      const on = j === this.index;
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-hidden', String(!on));
      s.querySelectorAll('a, button').forEach((el) => (on ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
    });
    this.dots.forEach((d, j) => d.setAttribute('aria-current', String(j === this.index)));
    this.style.setProperty('--rgs-slide-index', this.index);
  }
  start() {
    const secs = Number(this.dataset.autoplay || 0);
    this.stop();
    if (!secs || !this.ready || reduceMotion.matches || this.slides.length < 2 || this.held || this.userPaused || document.hidden) return;
    this.timer = setInterval(() => this.show(this.index + 1), secs * 1000);
    this.live?.setAttribute('aria-live', 'off');
  }
  stop() {
    clearInterval(this.timer);
    this.timer = null;
    this.live?.setAttribute('aria-live', 'polite');
  }
  // Resume unless hovered or keyboard-focused. A mouse click leaves focus on a dot or the pause button
  // without :focus-visible, so moving the mouse away still resumes.
  restart() {
    this.stop();
    let kbFocus = false;
    try { kbFocus = !!this.querySelector(':focus-visible'); } catch { kbFocus = this.contains(document.activeElement); }
    if (!this.matches(':hover') && !kbFocus) this.start();
  }
  syncToggle() {
    if (!this.toggle) return;
    const paused = !!this.userPaused;
    this.toggle.classList.toggle('is-paused', paused);
    this.toggle.setAttribute('aria-label', (paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause) || (paused ? 'Play slideshow' : 'Pause slideshow'));
  }
}
/* ---------------- Rotating heroes (sections/rgs-hero, "Rotate with the hero above") ----------------
   Rubicon heroes placed directly under another Rubicon hero with "Rotate with the hero above" ticked
   (data-rgs-hero-follow) join that hero in one slideshow. Each keeps all its own settings; the top
   hero's Slideshow settings (data-rgs-hero-*) run the group. The Shopify section wrappers are moved
   into an <rgs-hero-group> that reuses RgsSlideshow. Followers stay hidden by CSS until grouped,
   so they never flash under the first hero; a follower with no hero above it shows as a normal hero.
   Not in the theme editor, where the heroes stay stacked so each can be edited. */
class RgsHeroGroup extends RgsSlideshow {
  show(i) {
    super.show(i);
    // Hidden heroes: out of the tab order and away from screen readers.
    this.slides.forEach((s, j) => { s.inert = j !== this.index; });
    // Followers' photos are lazy: always start the shown hero's, and the next one's once prefetching is on.
    this.eager(this.slides[this.index]);
    if (this.loadAhead) this.eager(this.slides[(this.index + 1) % this.slides.length]);
  }
  eager(slide) {
    slide?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }
}

const rgsGroupHeroes = () => {
  const heroOf = (el) => (el instanceof Element && el.classList.contains('shopify-section')
    ? el.querySelector(':scope > .rgs-hero[data-rgs-hero]') : null);
  const leaders = [...document.querySelectorAll('.shopify-section > .rgs-hero[data-rgs-hero]:not([data-rgs-hero-follow])')];
  leaders.forEach((hero) => {
    const lead = hero.parentElement;
    const members = [lead];
    let next = lead.nextElementSibling;
    while (heroOf(next)?.hasAttribute('data-rgs-hero-follow')) { members.push(next); next = next.nextElementSibling; }
    if (members.length < 2) return;
    const d = hero.dataset;
    const secs = Number(d.rgsHeroAutoplay || 0);
    const n = members.length;
    const group = document.createElement('rgs-hero-group');
    group.className = `rgs-hero-group rgs-hero__frame${d.rgsHeroTransition === 'slide' ? ' rgs-slideshow--slide' : ''}`;
    group.dataset.autoplay = String(secs);
    group.setAttribute('role', 'region');
    group.setAttribute('aria-roledescription', 'carousel');
    group.setAttribute('aria-label', d.rgsHeroLabel || 'Featured');
    group.tabIndex = -1;
    const track = document.createElement('div');
    track.className = 'rgs-hero__track rgs-hero-group__track';
    track.setAttribute('data-rgs-live', '');
    track.setAttribute('aria-live', secs ? 'off' : 'polite');
    group.append(track);
    const marker = document.createComment('rgs-hero-group');
    lead.before(marker);
    members.forEach((m, i) => {
      m.classList.add('rgs-slide', 'rgs-hero-group__item');
      if (i === 0) m.classList.add('is-active');
      m.setAttribute('role', 'group');
      m.setAttribute('aria-roledescription', 'slide');
      m.setAttribute('aria-label', `${i + 1} of ${n}`);
      // content-visibility (data-rgs-defer) would leave a hero blank when it rotates in.
      m.querySelectorAll('[data-rgs-defer]').forEach((el) => el.removeAttribute('data-rgs-defer'));
      track.append(m);
    });
    // Same controls as a hero with Slide blocks (sections/rgs-hero.liquid).
    if (d.rgsHeroArrows === 'true') {
      group.insertAdjacentHTML('beforeend',
        '<button type="button" class="rgs-arrow rgs-arrow--prev rgs-slideshow__arrow" data-rgs-prev aria-label="Previous slide"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button>'
        + '<button type="button" class="rgs-arrow rgs-arrow--next rgs-slideshow__arrow" data-rgs-next aria-label="Next slide"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>');
    }
    const dots = d.rgsHeroDots === 'true';
    if (dots || secs) {
      const bar = document.createElement('div');
      bar.className = 'rgs-slideshow__dots rgs-hero__dots';
      if (dots) {
        for (let i = 1; i <= n; i += 1) {
          bar.insertAdjacentHTML('beforeend', `<button type="button" class="rgs-dot" data-rgs-dot aria-label="Go to slide ${i}" aria-current="${i === 1}"></button>`);
        }
      }
      if (secs) {
        bar.insertAdjacentHTML('beforeend', '<button type="button" class="rgs-hero__toggle" data-rgs-toggle aria-label="Pause slideshow" data-label-pause="Pause slideshow" data-label-play="Play slideshow">'
          + '<svg class="rgs-hero__toggle-pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6v12M15 6v12"/></svg>'
          + '<svg class="rgs-hero__toggle-play" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7z"/></svg></button>');
      }
      group.append(bar);
    }
    marker.replaceWith(group);
    // Fetch the next hero's photo shortly before it rotates in (lazy images in a hidden slide may never
    // start on their own): after page load, once the browser is idle, then 2 s later, so it never
    // competes with the first hero's photo or the page's scripts (page speed).
    const ahead = () => { group.loadAhead = true; group.eager(group.slides?.[group.index ?? 0]); group.eager(group.slides?.[((group.index ?? 0) + 1) % (group.slides?.length || 1)]); };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => setTimeout(ahead, 2000), { timeout: 3000 }) : setTimeout(ahead, 3000));
    if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
  });
  // Followers with no hero directly above them show as normal heroes.
  document.querySelectorAll('.rgs-hero[data-rgs-hero-follow]').forEach((h) => {
    if (!h.closest('rgs-hero-group')) h.classList.add('is-solo');
  });
};
// Runs before the slideshow elements are defined, so moving a hero that has its own Slide blocks
// doesn't set its slideshow up twice; each is set up once, in its new place.
try {
  if (!window.Shopify?.designMode && document.querySelector('.rgs-hero[data-rgs-hero-follow]')) rgsGroupHeroes();
} catch (err) {
  console.warn('rgs-hero-group', err);
  document.querySelectorAll('.rgs-hero[data-rgs-hero-follow]').forEach((h) => { if (!h.closest('rgs-hero-group')) h.classList.add('is-solo'); });
}
if (!customElements.get('rgs-slideshow')) customElements.define('rgs-slideshow', RgsSlideshow);
if (!customElements.get('rgs-hero-group')) customElements.define('rgs-hero-group', RgsHeroGroup);

/* ---------------- Tabs (product tabs, homepage product tabs) ---------------- */
class RgsTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll(':scope [role="tab"]')].filter((t) => t.closest('rgs-tabs') === this);
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        const last = this.tabs.length - 1;
        const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: last }[e.key];
        if (next === undefined) return;
        e.preventDefault();
        this.select(next < 0 ? last : next > last ? 0 : next, true);
      });
    });
  }
  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
      if (on && focus) tab.focus();
      if (on) tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });
    this.dispatchEvent(new CustomEvent('rgs-tabs:change', { detail: { index }, bubbles: true }));
  }
}
if (!customElements.get('rgs-tabs')) customElements.define('rgs-tabs', RgsTabs);

/* ---------------- Mega menu (sections/rgs-mega-menu) ----------------
   Items with a Horizon dropdown get the Rubicon columns added beside Horizon's links.
   Items without one (no child links in Navigation) get their own full-width panel under the
   header, opened on hover or keyboard focus, like Rubix's mega menu. Desktop only.
   Pages normally print an empty <rgs-mega-menu data-lazy>: the columns' markup is fetched from
   /search?section_id=<section id> when the page is idle, or on the first hover or focus in the
   header, whichever comes first. If that fetch fails, Horizon's own dropdowns work as usual. */
class RgsMegaMenu extends HTMLElement {
  connectedCallback() {
    // Text-only dropdowns: start the links under the hovered menu item, not at the page edge.
    if (!RgsMegaMenu.alignBound) {
      RgsMegaMenu.alignBound = true;
      const onEnter = (e) => {
        const li = e.target instanceof Element ? e.target.closest('header-menu .menu-list__list-item') : null;
        if (li && li.getAttribute('slot') !== 'more' && li.getAttribute('slot') !== 'overflow') RgsMegaMenu.alignPlain(li);
      };
      document.addEventListener('pointerover', onEnter);
      document.addEventListener('focusin', onEnter);
    }
    // Horizon re-renders the header menu after load (section hydration), which drops the
    // injected columns, so watch the header and inject again whenever they go missing.
    this.observer = new MutationObserver(() => {
      clearTimeout(this.reinjectTimer);
      this.reinjectTimer = setTimeout(() => { if (this.needsInject()) this.inject(); }, 50);
    });
    // The columns only show on desktop (990px and up). On phones and tablets, building them at load
    // cost main-thread time and downloaded the banner photos for nothing, so wait until the screen
    // is wide enough (a rotated tablet or a resized window), then build them as before.
    // The theme editor always builds them.
    this.started = false;
    const start = () => {
      if (this.started) return;
      this.started = true;
      if (this.hasAttribute('data-lazy')) this.scheduleLoad();
      else this.inject();
    };
    this.desktopMq = window.matchMedia('(min-width: 990px)');
    if (this.desktopMq.matches || window.Shopify?.designMode) start();
    else {
      this.onMq = (e) => { if (e.matches) { this.desktopMq.removeEventListener('change', this.onMq); start(); } };
      this.desktopMq.addEventListener('change', this.onMq);
    }
    this.onSectionLoad = () => requestAnimationFrame(() => { if (this.started) this.inject(); });
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }
  disconnectedCallback() {
    if (this.onMq) this.desktopMq?.removeEventListener('change', this.onMq);
    this.loadAbort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.reinjectTimer);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    RgsMegaMenu.clear();
  }
  // Lazy markup: load at the first idle moment after the page has loaded, or straight away on the
  // first hover or keyboard focus in the header (so a fast visitor doesn't wait for idle).
  scheduleLoad() {
    this.loadAbort = new AbortController();
    const { signal } = this.loadAbort;
    let pending = false;
    const go = () => {
      if (pending || signal.aborted) return;
      pending = true;
      this.load().then((ok) => {
        pending = false;
        if (!ok || !this.isConnected || signal.aborted) return;
        this.loadAbort.abort();
        this.inject();
      });
    };
    const idle = () => {
      if (signal.aborted) return;
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 4000 });
      else setTimeout(go, 1500);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true, signal });
    const onIntent = (e) => {
      if (e.target instanceof Element && e.target.closest('header-menu, #header-component, header')) go();
    };
    document.addEventListener('pointerover', onIntent, { passive: true, signal });
    document.addEventListener('focusin', onIntent, { signal });
  }
  // Fetches this section's full markup once and keeps its <template>s. Resolves true when there are
  // columns to build; a failed fetch can be retried twice (on a later hover).
  load() {
    if (!this.hasAttribute('data-lazy')) return Promise.resolve(true);
    if (this.loading) return this.loading;
    this.failures = this.failures || 0;
    if (this.failures > 2 || !this.dataset.sectionId) return Promise.resolve(false);
    const base = window.Shopify?.routes?.root || '/';
    this.loading = fetch(`${base}search?section_id=${encodeURIComponent(this.dataset.sectionId)}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((html) => {
        const src = new DOMParser().parseFromString(html, 'text/html').querySelector('rgs-mega-menu');
        const tpls = src ? [...src.querySelectorAll(':scope > template[data-menu-item]')] : [];
        if (!tpls.length) return false;
        this.replaceChildren(...tpls.map((t) => document.importNode(t, true)));
        this.removeAttribute('data-lazy');
        return true;
      })
      .catch(() => {
        this.failures += 1;
        this.loading = null;
        return false;
      });
    return this.loading;
  }
  static alignPlain(li) {
    const grid = li.querySelector(':scope > .menu-list__submenu .mega-menu__grid');
    if (!grid || li.querySelector('.rgs-mm-wrap') || !window.matchMedia('(min-width: 990px)').matches) {
      if (grid) grid.style.translate = '';
      return;
    }
    const cols = grid.querySelectorAll('.mega-menu__column');
    const link = li.querySelector('.menu-list__link');
    if (!cols.length || !link) return;
    grid.style.translate = '';
    const base = grid.getBoundingClientRect();
    if (!base.width) return;
    const right = Math.max(...[...cols].map((c) => c.getBoundingClientRect().right)) - base.left;
    const shift = Math.min(link.getBoundingClientRect().left - base.left, base.width - right);
    grid.style.translate = shift > 0 ? `${Math.round(shift)}px 0` : '';
  }
  static items() {
    return [...document.querySelectorAll('header-menu .menu-list__list-item')].filter((li) => li.getAttribute('slot') !== 'more');
  }
  static findItem(all, key) {
    const n = Number.parseInt(key, 10);
    return String(n) === key
      ? all[n - 1]
      : all.find((item) => item.querySelector('.menu-list__link-title')?.textContent.trim().toLowerCase() === key);
  }
  needsInject() {
    const all = RgsMegaMenu.items();
    return [...this.querySelectorAll('template[data-menu-item]')].some((tpl) => {
      const li = RgsMegaMenu.findItem(all, tpl.dataset.menuItem.trim().toLowerCase());
      if (!li || li.hasAttribute('data-rgs-mm-trigger')) return false;
      // Also repair a half-rebuilt dropdown: no wrap, more than one, or links outside the wrap.
      const wraps = li.querySelectorAll('.rgs-mm-wrap');
      return wraps.length !== 1 || !wraps[0].querySelector(':scope > .mega-menu__grid')
        || li.querySelectorAll('.mega-menu__grid').length > 1;
    });
  }
  watch() {
    const root = document.querySelector('header-menu')?.closest('.shopify-section, header, #header-component');
    if (root) this.observer.observe(root, { childList: true, subtree: true });
  }
  static clear() {
    RgsMegaMenu.sizeObs?.disconnect();
    document.querySelectorAll('.rgs-mm-wrap').forEach((wrap) => {
      const grid = wrap.querySelector(':scope > .mega-menu__grid');
      if (grid) wrap.replaceWith(grid); else wrap.remove();
    });
    document.querySelectorAll('.rgs-mm-panel').forEach((panel) => panel.remove());
    document.querySelectorAll('[data-rgs-mm-trigger]').forEach((li) => {
      li.rgsMmAbort?.abort();
      li.removeAttribute('data-rgs-mm-trigger');
      li.querySelector(':scope .menu-list__link')?.removeAttribute('aria-expanded');
    });
  }
  inject() {
    this.observer?.disconnect();
    RgsMegaMenu.clear();
    const all = RgsMegaMenu.items();
    const replace = this.dataset.replaceLinks === 'true';
    const style = this.getAttribute('style') || '';
    this.querySelectorAll('template[data-menu-item]').forEach((tpl) => {
      const key = tpl.dataset.menuItem.trim().toLowerCase();
      const li = RgsMegaMenu.findItem(all, key);
      if (!li) return;
      const grid = li.querySelector('.menu-list__submenu .mega-menu__grid');
      if (grid) {
        const wrap = document.createElement('div');
        wrap.className = `rgs-mm-wrap${replace ? ' rgs-mm-wrap--replace' : ''}`;
        wrap.setAttribute('style', style);
        // A text-only alignment shift applied before this item got its columns would push the
        // links behind the products, so clear it.
        grid.style.translate = '';
        grid.replaceWith(wrap);
        wrap.append(grid, tpl.content.cloneNode(true));
        RgsMegaMenu.bindGroups(wrap);
        RgsMegaMenu.markCurrent(wrap);
        const load = () => RgsMegaMenu.upgrade(wrap);
        if (RgsMegaMenu.best?.size || li.matches(':hover') || li.contains(document.activeElement)) load();
        li.addEventListener('pointerenter', load, { once: true });
        li.addEventListener('focusin', load, { once: true });
        const sub = wrap.closest('.menu-list__submenu');
        if (sub && 'ResizeObserver' in window) {
          RgsMegaMenu.sizeObs = RgsMegaMenu.sizeObs || new ResizeObserver(RgsMegaMenu.fitOpen);
          RgsMegaMenu.sizeObs.observe(sub);
        }
      } else {
        this.buildPanel(li, tpl, style);
      }
    });
    this.watch();
  }
  // Horizon measures the open dropdown once when it opens (--submenu-height and
  // --full-open-header-height on the header) and only watches it for 500ms. The lazy columns and
  // the best-seller lists arrive later and change its height, which left the header short and the
  // columns spilling over the page, so follow the open dropdown's size. The first callback after
  // observe() covers columns injected while the item is already open. Overflow ("More") items are
  // measured differently by Horizon, so they are left alone.
  static fitOpen(entries) {
    entries.forEach(({ target: sub }) => {
      const li = sub.closest('.menu-list__list-item');
      const header = sub.closest('header-component');
      // Horizon clears inert and sets aria-expanded on the open item; both are reset on close or
      // switch (data-active is not removed on a switch, so it isn't used here).
      if (!li || !header || sub.inert || li.getAttribute('slot') === 'overflow' || !li.querySelector('[aria-expanded="true"]')) return;
      const h = sub.offsetHeight;
      const cur = Number.parseFloat(header.style.getPropertyValue('--submenu-height')) || 0;
      if (!h || h === cur) return;
      header.style.setProperty('--submenu-height', `${h}px`);
      const full = Number.parseFloat(header.style.getPropertyValue('--full-open-header-height')) || 0;
      if (full) header.style.setProperty('--full-open-header-height', `${full + h - cur}px`);
    });
  }
  // Hovering or focusing a dropdown link shows the column group made for it (data-sub is
  // the link title); other links show the default group. A short hover delay stops the
  // groups flipping while the pointer crosses other links on its way to the columns.
  // Swap each product list in the dropdown for its collection's best sellers, fetched once per
  // collection through sections/rgs-mega-best and cached for the rest of the visit.
  static upgrade(root) {
    RgsMegaMenu.best = RgsMegaMenu.best || new Map();
    const base = window.Shopify?.routes?.root || '/';
    root.querySelectorAll('.rgs-mm__products[data-collection]:not([data-best])').forEach((list) => {
      const handle = list.dataset.collection;
      if (!handle) return;
      if (!RgsMegaMenu.best.has(handle)) {
        RgsMegaMenu.best.set(handle, fetch(`${base}collections/${encodeURIComponent(handle)}?sort_by=best-selling&section_id=rgs-mega-best`)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.rgs-mm__products > li')])
          .catch(() => []));
      }
      RgsMegaMenu.best.get(handle).then((items) => {
        if (!items.length || !list.isConnected) return;
        const limit = Number.parseInt(list.dataset.limit, 10) || items.length;
        const showPrice = !!list.querySelector('.rgs-mm__price') || !list.children.length;
        list.replaceChildren(...items.slice(0, limit).map((li) => {
          const copy = li.cloneNode(true);
          if (!showPrice) copy.querySelectorAll('.rgs-mm__price').forEach((el) => el.remove());
          return copy;
        }));
        list.dataset.best = '';
      });
    });
  }
  // The lazily fetched markup was rendered for the search page, so mark links to this page here.
  static markCurrent(root) {
    root.querySelectorAll('.rgs-mm__links a[href]').forEach((a) => {
      if (a.pathname === window.location.pathname) a.setAttribute('aria-current', 'page');
    });
  }
  static bindGroups(wrap) {
    const groups = [...wrap.querySelectorAll('.rgs-mm__group')];
    if (groups.length < 2) return;
    let timer;
    const show = (sub) => {
      const target = groups.find((g) => g.dataset.sub === sub) || groups[0];
      groups.forEach((g) => g.classList.toggle('is-active', g === target));
    };
    const keyOf = (target) => {
      const link = target instanceof Element ? target.closest('.mega-menu__link') : null;
      return link && wrap.contains(link) ? link.textContent.trim().toLowerCase() : null;
    };
    wrap.addEventListener('pointerover', (e) => {
      const key = keyOf(e.target);
      clearTimeout(timer);
      if (key !== null) timer = setTimeout(() => show(key), 120);
    });
    wrap.addEventListener('focusin', (e) => {
      const key = keyOf(e.target);
      if (key !== null) { clearTimeout(timer); show(key); }
    });
    wrap.addEventListener('pointerleave', () => { clearTimeout(timer); show(''); });
  }
  buildPanel(li, tpl, style) {
    const host = li.closest('#header-component') || li.closest('header') || document.body;
    const link = li.querySelector('.menu-list__link');
    const panel = document.createElement('div');
    panel.className = 'rgs-mm-panel';
    panel.setAttribute('style', style);
    panel.hidden = true;
    const inner = document.createElement('div');
    inner.className = 'rgs-mm-panel__inner';
    inner.append(tpl.content.cloneNode(true));
    panel.append(inner);
    host.append(panel);
    RgsMegaMenu.markCurrent(panel);
    li.setAttribute('data-rgs-mm-trigger', '');
    link?.setAttribute('aria-expanded', 'false');
    const ac = new AbortController();
    li.rgsMmAbort = ac;
    const { signal } = ac;
    let timer;
    const open = () => {
      clearTimeout(timer);
      RgsMegaMenu.upgrade(panel);
      document.querySelectorAll('.rgs-mm-panel:not([hidden])').forEach((p) => { if (p !== panel) p.hidden = true; });
      panel.hidden = false;
      link?.setAttribute('aria-expanded', 'true');
    };
    const close = (delay = 160) => {
      clearTimeout(timer);
      timer = setTimeout(() => { panel.hidden = true; link?.setAttribute('aria-expanded', 'false'); }, delay);
    };
    const desktop = () => window.matchMedia('(min-width: 990px)').matches;
    li.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && desktop()) open(); }, { signal });
    li.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') close(); }, { signal });
    panel.addEventListener('pointerenter', () => clearTimeout(timer), { signal });
    panel.addEventListener('pointerleave', () => close(), { signal });
    li.addEventListener('focusin', () => { if (desktop()) open(); }, { signal });
    const onFocusOut = (e) => { if (!li.contains(e.relatedTarget) && !panel.contains(e.relatedTarget)) close(0); };
    li.addEventListener('focusout', onFocusOut, { signal });
    panel.addEventListener('focusout', onFocusOut, { signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !panel.hidden) { close(0); link?.focus(); }
    }, { signal });
    // Built while the pointer or keyboard focus is already on the item (lazy markup just arrived).
    if (desktop() && (li.matches(':hover') || li.contains(document.activeElement))) open();
    // Keyboard: Tab from the menu link moves into the panel.
    link?.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && !e.shiftKey && !panel.hidden) {
        const first = panel.querySelector('a, button');
        if (first) { e.preventDefault(); first.focus(); }
      }
    }, { signal });
  }
}
if (!customElements.get('rgs-mega-menu')) customElements.define('rgs-mega-menu', RgsMegaMenu);

/* ---------------- Free-shipping bar (sections/rgs-shipping-bar) ---------------- */
class RgsShippingBar extends HTMLElement {
  connectedCallback() {
    this.total = Number(this.dataset.total) || 0;
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    this.threshold = Math.round((Number(this.dataset.threshold) || 0) * rate);
    if (!this.threshold) return;
    this.render();
    this.onCart = (event) => {
      Promise.resolve(event?.promise)
        .then(() => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }))
        .then((r) => r.json())
        .then((cart) => { this.total = cart.total_price; this.render(); })
        .catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    // Horizon re-renders the drawer on updates; put the bar back whenever it is replaced.
    // Page changes elsewhere (apps, carousels) only need a quick check that every bar is still there.
    this.mo = new MutationObserver(() => { if (!this.pending) { this.pending = true; requestAnimationFrame(() => { this.pending = false; this.render(true); }); } });
    this.mo.observe(document.body, { childList: true, subtree: true });
  }
  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    this.mo?.disconnect();
    document.querySelectorAll('.rgs-ship').forEach((el) => el.remove());
  }
  money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const n = cents / 100;
    const withSep = (v, d, t, s) => v.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, t).replace(/\.(\d+)$/, `${s}$1`);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: withSep(n, 2, ',', '.'),
      amount_no_decimals: withSep(n, 0, ',', '.'),
      amount_with_comma_separator: withSep(n, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withSep(n, 0, '.', ','),
    })[k] ?? withSep(n, 2, ',', '.'));
  }
  render(onlyMissing = false) {
    const targets = [];
    // This runs after every page change, so the lookups start from the drawer and the class list
    // rather than matching selectors across the whole page.
    const drawer = this.dataset.inDrawer === 'true' && document.getElementById('cart-drawer');
    if (drawer) targets.push(...drawer.querySelectorAll('.cart-drawer__summary'));
    // On the cart page, go inside the padded summary column: the outer .cart-page__summary is a grid
    // whose rows the summary spans, so a bar placed there sat on top of the checkout buttons.
    if (this.dataset.onPage === 'true') [...document.getElementsByClassName('cart-page__summary')].forEach((s) => targets.push(s.querySelector('.cart-summary__inner') || s));
    if (!targets.length) return;
    // After a page change the total is unchanged, so only a replaced summary (no bar yet) needs drawing.
    if (onlyMissing && targets.every((host) => host.querySelector(':scope > .rgs-ship'))) return;
    const left = Math.max(0, this.threshold - this.total);
    const pct = Math.min(100, Math.round((this.total / this.threshold) * 100));
    const msg = left > 0 ? (this.dataset.away || '').replace('[amount]', this.money(left)) : this.dataset.done || '';
    targets.forEach((host) => {
      let bar = host.querySelector(':scope > .rgs-ship');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'rgs-ship';
        bar.setAttribute('style', this.getAttribute('style') || '');
        bar.innerHTML = '<p class="rgs-ship__msg" role="status"></p><div class="rgs-ship__track" aria-hidden="true"><span class="rgs-ship__fill"></span></div>';
        host.prepend(bar);
      }
      bar.classList.toggle('rgs-ship--done', left === 0);
      const text = bar.querySelector('.rgs-ship__msg');
      if (text.textContent !== msg) text.textContent = msg;
      bar.querySelector('.rgs-ship__fill').style.width = `${pct}%`;
    });
  }
}
if (!customElements.get('rgs-shipping-bar')) customElements.define('rgs-shipping-bar', RgsShippingBar);

/* ---------------- Recently viewed (sections/rgs-recently-viewed) ---------------- */
class RgsRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const list = this.querySelector('[data-rgs-recent-list]');
    if (!list || !this.hidden) return;
    let ids = [];
    try { ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]'); } catch { ids = []; }
    const exclude = this.dataset.exclude;
    ids = ids.filter((id) => id && String(id) !== exclude).slice(0, Number(this.dataset.limit) || 4);
    if (!ids.length) return;
    const q = encodeURIComponent(ids.map((id) => `id:${id}`).join(' OR '));
    try {
      const res = await fetch(`${root}search?q=${q}&type=product&options%5Bprefix%5D=none&section_id=${this.dataset.sectionId}`);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const cards = [...doc.querySelectorAll('[data-rgs-recent-list] > .rgs-card')];
      if (!cards.length) return;
      cards.sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId));
      list.replaceChildren(...cards.map((c) => document.importNode(c, true)));
      this.hidden = false;
    } catch { /* leave the row hidden */ }
  }
}
if (!customElements.get('rgs-recently-viewed')) customElements.define('rgs-recently-viewed', RgsRecentlyViewed);

/* ---------------- Countdown (snippets/rgs-countdown-timer) ---------------- */
class RgsCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Date.parse(this.dataset.end);
    if (Number.isNaN(this.end)) return;
    this.els = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  tick() {
    const left = Math.max(0, this.end - Date.now());
    if (left === 0) {
      clearInterval(this.timer);
      if (this.dataset.expired === 'message' && this.dataset.message) {
        const p = document.createElement('p');
        p.className = 'rgs-cd__ended';
        p.textContent = this.dataset.message;
        this.replaceWith(p);
      } else if (!window.Shopify?.designMode) {
        (this.closest('[data-rgs-countdown-host]') || this).hidden = true;
      }
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    const sec = Math.floor(left / 1000);
    this.els.d.textContent = pad(Math.floor(sec / 86400));
    this.els.h.textContent = pad(Math.floor((sec % 86400) / 3600));
    this.els.m.textContent = pad(Math.floor((sec % 3600) / 60));
    this.els.s.textContent = pad(sec % 60);
  }
}
if (!customElements.get('rgs-countdown')) customElements.define('rgs-countdown', RgsCountdown);

/* On desktop (990px and up) Horizon locks the window and scrolls .page-wrapper instead, so scroll
   code must use whichever one actually scrolls. */
const rgsScroller = () => {
  const pw = document.querySelector('.page-wrapper');
  return pw && /auto|scroll/.test(getComputedStyle(pw).overflowY) ? pw : window;
};

/* ---------------- Site extras: mobile bar + go to top (sections/rgs-site-extras) ---------------- */
class RgsSiteExtras extends HTMLElement {
  connectedCallback() {
    this.top = this.querySelector('[data-rgs-top]');
    this.count = this.querySelector('[data-rgs-cart-count]');
    if (this.top) {
      // Listen in the capture phase so scrolling of .page-wrapper (desktop) is caught as well as the window.
      // The event says which one scrolled, so there's no style lookup per scroll; other scrolling
      // (carousels, drawers) is ignored, and the button updates at most once per frame.
      let queued = null;
      this.onScroll = (e) => {
        const t = e && e.target;
        let read;
        if (!t || t === document || t === document.documentElement || t === document.body) read = () => window.scrollY;
        else if (t instanceof Element && t.classList.contains('page-wrapper')) read = () => t.scrollTop;
        else return;
        if (queued) { queued = read; return; }
        queued = read;
        requestAnimationFrame(() => { const r = queued; queued = null; this.top.hidden = r() < 600; });
      };
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      // The button starts hidden (markup). No position check at load: it forced a style calculation
      // while the page was loading, and a restored scroll position fires a scroll event anyway.
      this.top.addEventListener('click', () => {
        rgsScroller().scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        document.querySelector('#MainContent, main')?.focus?.({ preventScroll: true });
      });
    }
    if (this.count) {
      this.onCart = (event) => {
        Promise.resolve(event?.promise)
          .then(() => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }))
          .then((r) => r.json())
          .then((cart) => { this.count.textContent = cart.item_count; this.count.hidden = cart.item_count === 0; })
          .catch(() => {});
      };
      document.addEventListener(StandardEvents.cartLinesUpdate, this.onCart);
    }
  }
  disconnectedCallback() {
    if (this.onScroll) document.removeEventListener('scroll', this.onScroll, { capture: true });
    if (this.onCart) document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCart);
  }
}
if (!customElements.get('rgs-site-extras')) customElements.define('rgs-site-extras', RgsSiteExtras);

/* <rgs-ticker>: rotates its .rgs-ticker__item children (top bar announcements). */
class RgsTicker extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.rgs-ticker__item')];
    if (this.items.length < 2) return;
    this.index = 0;
    const ms = (parseInt(this.dataset.speed, 10) || 6) * 1000;
    const next = () => {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    };
    afterLoad(() => {
      if (this.isConnected) this.timer = setInterval(() => { if (!this.matches(':hover')) next(); }, ms);
    });
  }
  disconnectedCallback() { clearInterval(this.timer); }
}
if (!customElements.get('rgs-ticker')) customElements.define('rgs-ticker', RgsTicker);

/* ---------------- Share buttons (blocks/rgs-share) ---------------- */
if (navigator.share) {
  const showNative = () => document.querySelectorAll('[data-rgs-share-native][hidden]').forEach((b) => { b.hidden = false; });
  showNative();
  document.addEventListener('shopify:section:load', showNative);
}
document.addEventListener('click', async (e) => {
  const native = e.target.closest('[data-rgs-share-native]');
  if (native) {
    try { await navigator.share({ title: native.dataset.title, url: native.dataset.url }); } catch { /* cancelled */ }
    return;
  }
  const copy = e.target.closest('[data-rgs-share-copy]');
  if (!copy) return;
  const status = copy.querySelector('[data-rgs-share-status]');
  const label = status?.textContent;
  try {
    await navigator.clipboard.writeText(copy.dataset.url);
    copy.classList.add('is-done');
    if (status) status.textContent = copy.dataset.done;
    copy.setAttribute('aria-live', 'polite');
    setTimeout(() => { copy.classList.remove('is-done'); if (status) status.textContent = label; }, 2000);
  } catch { window.prompt('Copy this link', copy.dataset.url); }
});

/* ---------------- Quick add ---------------- */
document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('.rgs-quick-add')) return;
  e.preventDefault();
  const btn = form.querySelector('button');
  const id = Number(form.querySelector('[name="id"]').value);
  const label = btn.dataset.label || btn.textContent;
  btn.dataset.label = label;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product',
    lines: [{ merchandiseId: String(id), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
    });
    const data = await res.json();
    const cart = await (await fetch(root + 'cart.js')).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: !res.ok, items: cart.items, itemCount: 1, source: 'rgs-quick-add', productId: form.dataset.productId },
    });
    btn.textContent = res.ok ? (btn.dataset.added || 'Added') : (data.description || 'Could not add');
  } catch (err) {
    deferred.reject(err);
    form.submit(); // fall back to a normal add to cart
    return;
  }
  btn.removeAttribute('aria-busy');
  setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 2200);
});

/* ---------------- Companion offer (blocks/rgs-companion) ----------------
   "Complete your setup" under Add to Cart: one second mat and an "Add both" button that adds the
   selected variant of this product and the companion together. The companion is the product's
   custom.companion_product metafield (from the block's JSON), else the first related recommendation
   with the same product type, else the block's fallback collection product. Only available variants
   without "Custom Artwork" in the title are offered, and products with an excluded tag (data-exclude:
   no-quick-add plus the block's list, e.g. "Custom Playmats" for the custom-art mats) never are. The block hides while the selected variant of
   this product is a Custom Artwork one (artwork mode). Product text is set with textContent only. */
try {
  const compOk = (v) => !!v && v.available !== false && !/custom artwork/i.test(v.title || '');
  const compUrl = (u, w) => {
    if (!u) return '';
    try { const x = new URL(u, location.href); x.searchParams.set('width', String(w)); return x.href; } catch { return ''; }
  };

  class RgsCompanion extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.ready = false;
      this.onVariant = () => { this.check(); clearTimeout(this.timer); this.timer = setTimeout(() => this.check(), 400); };
      document.addEventListener('change', this.onVariant, true);
      // Artwork mode (below) toggles a class on <html>; follow it.
      this.htmlObserver = new MutationObserver(() => this.check());
      this.htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      if (StandardEvents && StandardEvents.productSelect) document.addEventListener(StandardEvents.productSelect, this.onVariant, true);
      this.querySelector('[data-rgs-comp-add]')?.addEventListener('click', () => this.add());
      this.querySelector('[data-rgs-comp-select]')?.addEventListener('change', () => this.showVariant());
      this.init().catch(() => { this.hidden = true; });
    }

    disconnectedCallback() {
      this.started = false;
      clearTimeout(this.timer);
      if (this.htmlObserver) this.htmlObserver.disconnect();
      document.removeEventListener('change', this.onVariant, true);
      if (StandardEvents && StandardEvents.productSelect) document.removeEventListener(StandardEvents.productSelect, this.onVariant, true);
    }

    usable(p) {
      if (!p || p.available === false) return null;
      if (String(p.id) === this.dataset.productId || p.handle === this.dataset.handle) return null;
      // Custom-art products (handles start "custom-") need artwork, so are never offered.
      if (/^custom-/i.test(p.handle || '')) return null;
      // "Complete your setup" offers an accessory, never a product of the same type (another mat).
      const type = (this.dataset.type || '').trim().toLowerCase();
      if (type && String(p.type || '').trim().toLowerCase() === type) return null;
      const exclude = (this.dataset.exclude || ',no-quick-add,').toLowerCase();
      if ((p.tags || []).some((t) => exclude.includes(`,${String(t).trim().toLowerCase()},`))) return null;
      const variants = (p.variants || []).filter(compOk);
      return variants.length ? { ...p, variants } : null;
    }

    async init() {
      const json = this.querySelector('[data-rgs-comp-data]');
      const data = json ? JSON.parse(json.textContent) : {};
      this.artwork = new Set((data.artwork || []).map(String));
      const pick = this.usable(data.meta) || this.usable(data.fallback);
      if (!this.isConnected) return;
      if (!pick) {
        if (this.hasAttribute('data-design-mode')) { this.classList.add('is-empty'); this.hidden = false; }
        else this.hidden = true;
        return;
      }
      this.render(pick);
      this.ready = true;
      this.check();
    }

    render(p) {
      this.product = p;
      const href = /^\/(?!\/)/.test(p.url || '') ? p.url : `${root}products/${encodeURIComponent(p.handle || '')}`;
      this.querySelectorAll('[data-rgs-comp-link]').forEach((a) => { a.href = href; });
      const title = this.querySelector('[data-rgs-comp-title]');
      if (title) title.textContent = p.title || '';
      const field = this.querySelector('[data-rgs-comp-field]');
      const select = this.querySelector('[data-rgs-comp-select]');
      const label = this.querySelector('[data-rgs-comp-label]');
      if (select) {
        select.replaceChildren(...p.variants.map((v) => {
          const o = document.createElement('option');
          o.value = String(v.id);
          o.textContent = v.title || '';
          return o;
        }));
      }
      if (label) label.textContent = `Options for ${p.title || 'the add-on'}`;
      if (field) field.hidden = p.variants.length < 2;
      this.showVariant();
    }

    variant() {
      const p = this.product;
      if (!p) return null;
      const select = this.querySelector('[data-rgs-comp-select]');
      const id = select && p.variants.length > 1 ? select.value : String(p.variants[0].id);
      return p.variants.find((v) => String(v.id) === id) || p.variants[0];
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
      } catch { return '$' + (cents / 100).toFixed(2); }
    }

    showVariant() {
      const v = this.variant();
      if (!v) return;
      const pct = Math.min(Math.max(Number(this.dataset.pct) || 0, 0), 100);
      const price = Number(v.price) || 0;
      const now = this.querySelector('[data-rgs-comp-price]');
      const was = this.querySelector('[data-rgs-comp-was]');
      if (now) now.textContent = this.money(pct > 0 ? Math.round(price * (100 - pct) / 100) : price);
      if (was) { was.textContent = pct > 0 ? this.money(price) : ''; was.hidden = !(pct > 0); }
      const img = this.querySelector('[data-rgs-comp-img]');
      const src = compUrl(v.image || this.product.image, 160);
      if (img) {
        if (src) { img.src = src; img.alt = this.product.title || ''; img.hidden = false; }
        else img.hidden = true;
      }
    }

    section() {
      return this.closest('.shopify-section') || document;
    }

    currentVariantId() {
      const sec = this.section();
      const input = sec.querySelector('product-form-component input[ref="variantId"]')
        || sec.querySelector('form[action*="/cart/add"]:not(.rgs-quick-add) input[name="id"]');
      const id = input && Number(input.value);
      return id || null;
    }

    // The variant picker shows the new choice at once; the hidden variant id input only changes
    // after Horizon's section fetch, so it is used only when there is no picker.
    isArtwork() {
      if (document.documentElement.classList.contains('rgs-artwork-mode')) return true;
      const sec = this.section();
      const values = [
        ...[...sec.querySelectorAll('variant-picker input[type="radio"]:checked')].map((i) => i.value),
        ...[...sec.querySelectorAll('variant-picker select')].map((s) => s.value),
      ];
      if (values.length) return values.some((v) => /custom artwork/i.test(v || ''));
      const id = this.currentVariantId();
      return !!(id && this.artwork && this.artwork.has(String(id)));
    }

    check() {
      if (!this.ready) return;
      this.hidden = this.isArtwork();
    }

    status(text, isError) {
      const el = this.querySelector('[data-rgs-comp-status]');
      if (!el) return;
      el.textContent = text;
      el.classList.toggle('is-error', !!isError);
    }

    async add() {
      const btn = this.querySelector('[data-rgs-comp-add]');
      const current = this.currentVariantId();
      const companion = this.variant();
      if (!btn || btn.disabled) return;
      if (!current || !companion) { this.status(this.dataset.error || 'Could not add to cart.', true); return; }
      const items = [{ id: current, quantity: 1 }, { id: Number(companion.id), quantity: 1 }];
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      this.status('', false);

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
        promise: deferred.promise,
      }));

      try {
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: !res.ok, items: cart.items, itemCount: 2, source: 'rgs-companion', productId: this.dataset.productId },
        });
        if (res.ok) this.status(this.dataset.added || 'Added to cart', false);
        else this.status(data.description || data.message || this.dataset.error || 'Could not add to cart.', true);
      } catch (err) {
        deferred.reject(err);
        this.status(this.dataset.error || 'Could not add to cart.', true);
      }
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }
  if (!customElements.get('rgs-companion')) customElements.define('rgs-companion', RgsCompanion);
} catch (err) { /* never break the rest of the module */ }

/* ---------------- Fit badges (blocks/rgs-fit-badges) ----------------
   "Fits the hexagon tube / a storage ring" pills. Horizon only re-renders the variant picker (and
   blocks with their own listeners) on a variant change, so the block prints a JSON map of
   variant ID -> two characters (tube, ring: y, n or - for blank; "p" is the product's own value) and
   this element switches the pills on Horizon's product select event. The variant ID comes from the
   event at once (event.detail.variantId), then again from the fetched variant once Horizon has it. */
try {
  class RgsFitBadges extends HTMLElement {
    connectedCallback() {
      if (this.scope) return;
      this.scope = this.closest('.shopify-section, dialog') || document;
      this.onSelect = (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (t && (t.closest('product-card') || t.closest('dialog') !== this.closest('dialog'))) return;
        const id = e.detail && e.detail.variantId;
        if (typeof id === 'string' || typeof id === 'number') this.show(String(id));
        if (e.promise && typeof e.promise.then === 'function') {
          e.promise.then((r) => {
            const rid = r && r.detail && r.detail.resource && r.detail.resource.id;
            this.show(rid != null ? String(rid) : (id != null ? String(id) : ''));
          }).catch(() => {});
        }
      };
      if (StandardEvents && StandardEvents.productSelect) this.scope.addEventListener(StandardEvents.productSelect, this.onSelect);
    }

    disconnectedCallback() {
      if (this.scope && StandardEvents && StandardEvents.productSelect) this.scope.removeEventListener(StandardEvents.productSelect, this.onSelect);
      this.scope = null;
    }

    // Read every time: a combined-listing switch morphs the block with a new product's map.
    map() {
      try { return JSON.parse(this.querySelector('[data-rgs-fit-map]')?.textContent || '{}') || {}; } catch { return {}; }
    }

    show(id) {
      const map = this.map();
      const value = String((id && map[id]) || map.p || '--');
      let any = false;
      ['tube', 'ring'].forEach((key, i) => {
        const c = value.charAt(i);
        const state = c === 'y' ? 'yes' : c === 'n' ? 'no' : '';
        const item = this.querySelector(`[data-rgs-fit="${key}"]`);
        if (item) { item.dataset.state = state; item.hidden = !state; }
        if (state) any = true;
      });
      this.hidden = !any;
    }
  }
  if (!customElements.get('rgs-fit-badges')) customElements.define('rgs-fit-badges', RgsFitBadges);
} catch (err) { /* never break the rest of the module */ }

/* ---------------- Game Night bundles ----------------
   <rgs-bundle-builder> (sections/rgs-bundle-builder.liquid) builds a bundle and adds it to the cart.
   Every bundle line carries hidden properties: _bundle (id), _bundle_role (mat | sleeves | storage),
   _bundle_mats and _bundle_players. The guard below runs on every page: it claims custom mats designed
   in Playmat Studio for their pending bundle (a cart line with a _Print_File_URL* property, matched by the
   variant chosen in the builder, else any variant of the same product), then applies the "part" code when every bundle has all its
   sleeves or all its storage (one per player), the "full" code when it has both, and removes the code otherwise.
   Percentages and codes come from the builder section's settings (cfg.pct, cfg.codes). Bundles are not
   eligible for other discount codes: a code the customer typed is removed while a bundle is in the cart.
   Pending bundles whose lines are in the cart claim first, then the newest; abandoned ones are pruned (bbPrune). */
const BB_CFG = 'rgsBundleCfg';
const BB_PENDING = 'rgsBundlePending';
const BB_MAX_AGE = 14 * 24 * 3600 * 1000;
const bbRead = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const bbWrite = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage off */ } };
const bbMoney = (cents, cur) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'USD' }).format(cents / 100); }
  catch { return '$' + (cents / 100).toFixed(2); }
};
const bbEsc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const bbJson = async (url, body) => {
  const res = await fetch(root + url, body ? {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
  } : { headers: { Accept: 'application/json' } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.description || data.message || 'Cart error (' + res.status + ')');
  return data;
};
// Claiming Studio mats: a Studio line has a print file property (_Print_File_URL, or _Print_File_URL_Front /
// _Print_File_URL_Back for double-sided mats). The customer can pick another variant (stitching) in Studio than
// in the builder, so a line matches a pending mat by exact variant, else (loose) by the same product path or
// Studio's reflective upgrade of the 24x14.
// Studio's reflective toggle swaps the product: pending mat's handle -> handles that also fill it (null prototype,
// so a handle such as "constructor" finds nothing).
const BB_UPGRADES = { __proto__: null, 'custom-24-x-14-playmat': ['custom-reflective-24-x-14-playmat'] };
const bbProdPath = (u) => {
  try { const m = new URL(u, 'https://x.invalid').pathname.match(/\/products\/([^/]+)\/?$/); return m ? m[1].toLowerCase() : ''; }
  catch { return ''; }
};
const bbStudio = (l) => Object.keys(l?.properties || {}).some((k) => k.startsWith('_Print_File_URL') && l.properties[k]);
// The open mats this line fills (exact variant first), up to its quantity. Empty when it can't be claimed.
const bbClaim = (l, mats, loose) => {
  if (!bbStudio(l) || l.properties._bundle) return [];
  const open = mats.filter((m) => !m.done);
  const exact = open.filter((m) => String(m.vid) === String(l.variant_id));
  if (!exact.length && !loose) return [];
  const path = bbProdPath(l.url);
  const same = path ? open.filter((m) => !exact.includes(m) && (bbProdPath(m.url) === path || (BB_UPGRADES[bbProdPath(m.url)] || []).includes(path))) : [];
  return [...exact, ...same].slice(0, Math.max(0, l.quantity || 0));
};

// _bundle ids that have lines in the cart.
const bbCartIds = (cart) => new Set((cart?.items || []).map((l) => l.properties?._bundle).filter(Boolean));
// Drops abandoned pending bundles: one that put lines in the cart (extras) but has none there now was emptied
// or removed. Without extras (custom mats only, or older entries without the flag) it can't be told from one
// still being designed, so it is dropped only when a newer pending bundle exists.
const bbPrune = (pend, ids) => pend.filter((b) => {
  const id = b.props?._bundle;
  if (!id || ids.has(id)) return true;
  if (b.extras) return false;
  return !pend.some((o) => o !== b && (o.t || 0) > (b.t || 0));
});
// Claim order: bundles with lines in the cart first, then the newest.
const bbOrderPending = (pend, ids) => [...pend].sort((a, b) =>
  (ids.has(b.props?._bundle) ? 1 : 0) - (ids.has(a.props?._bundle) ? 1 : 0) || (b.t || 0) - (a.t || 0));

const BundleGuard = {
  running: null,
  pending() {
    const now = Date.now();
    return bbRead(BB_PENDING, []).filter((b) => b && now - (b.t || 0) < BB_MAX_AGE && b.mats?.some((m) => !m.done));
  },
  run() {
    // One run at a time; a run requested during another waits for it.
    this.running = (this.running || Promise.resolve()).then(() => this._run()).catch((e) => console.warn('[rgs-bundle]', e));
    return this.running;
  },
  async _run() {
    const cfg = bbRead(BB_CFG, null);
    if (!cfg) return;
    let cart = await bbJson('cart.js');
    let changed = false;

    // 1. Claim designed custom mats for their bundle: exact-variant matches across all bundles first,
    //    then any variant of the same product. The line keeps its own variant. Abandoned pending bundles
    //    are dropped first; bundles already in the cart, then the newest, claim first.
    let pend = bbPrune(this.pending(), bbCartIds(cart));
    for (let guard = 0; guard < 12; guard++) {
      let claimed = false;
      const order = bbOrderPending(pend, bbCartIds(cart));
      for (const loose of [false, true]) {
        for (const b of order) {
          let fill = [];
          const line = cart.items.find((l) => (fill = bbClaim(l, b.mats, loose)).length);
          if (!line) continue;
          fill.forEach((m) => { m.done = true; });
          cart = await bbJson('cart/change.js', {
            id: line.key, quantity: line.quantity,
            properties: { ...line.properties, ...b.props, _bundle_role: 'mat' },
          });
          claimed = changed = true;
          break;
        }
        if (claimed) break;
      }
      if (!claimed) break;
    }
    bbWrite(BB_PENDING, pend.filter((b) => b.mats.some((m) => !m.done)));
    pend = this.pending();

    // 2. Work out the discount every bundle in the cart qualifies for.
    const groups = new Map();
    for (const l of cart.items) {
      const p = l.properties || {};
      if (!p._bundle) continue;
      const g = groups.get(p._bundle) || { name: String(p.Bundle || 'Bundle').replace(' ' + p._bundle, ''), mats: 0, sleeves: 0, storage: 0, needMats: +p._bundle_mats || 1, players: +p._bundle_players || 1 };
      if (p._bundle_role === 'mat') g.mats += l.quantity;
      else if (p._bundle_role === 'sleeves') g.sleeves += l.quantity;
      else if (p._bundle_role === 'storage') g.storage += l.quantity;
      groups.set(p._bundle, g);
    }
    // Levels: 0 none, 1 sleeves or storage (part), 2 both (full).
    const pct = cfg.pct || { part: 10, full: 15 };
    let level = groups.size ? 2 : 0;
    const notes = [];
    for (const g of groups.values()) {
      const matsOk = g.mats >= g.needMats;
      // Storage is one per player, so a two-player mat needs two rings or tubes.
      const sl = g.sleeves >= g.players, st = g.storage >= g.players;
      const lvl = !matsOk ? 0 : sl && st ? 2 : sl || st ? 1 : 0;
      level = Math.min(level, lvl);
      if (!matsOk) continue; // still designing: the pending note covers it
      const want = `${lvl === 0 ? pct.part : pct.full}%`;
      if (lvl < 2 && !sl) notes.push(`Add sleeves for every player in your ${bbEsc(g.name)} bundle (${g.players - g.sleeves} more) to save ${want}.`);
      else if (lvl < 2 && !st) notes.push(`Add a ring or tube for every player in your ${bbEsc(g.name)} bundle (${g.players - g.storage} more) to save ${want}.`);
    }

    // 3. Apply or remove our code. Bundles are not eligible for other discount codes: while a bundle is in
    //    the cart, a code the customer typed is removed and the bundle discount they earned goes back on.
    //    (Shopify's checkout has its own code box this can't reach; there a typed code replaces the bundle
    //    code instead, because the bundle codes don't combine with other discounts.)
    const ours = [cfg.codes?.part, cfg.codes?.full].filter(Boolean).map((c) => c.toUpperCase());
    const current = (cart.discount_codes || []).map((d) => String(d.code || '').toUpperCase());
    const others = current.filter((c) => !ours.includes(c));
    const want = level ? String(cfg.codes?.[level === 2 ? 'full' : 'part'] || '').toUpperCase() : '';
    if (others.length && groups.size) {
      await bbJson('cart/update.js', { discount: want });
      try {
        sessionStorage.setItem('rgsBundleCouponNote', `Discount codes can't be used on orders with a bundle, so ${others.map(bbEsc).join(', ')} was removed.${
          want ? ' Your bundle discount is applied instead.' : ' To use the code, remove the bundle items from your cart.'}`);
      } catch { /* storage off */ }
      changed = true;
    } else if (!others.length) {
      const hasOurs = current.filter((c) => ours.includes(c));
      if ((want && !(hasOurs.length === 1 && hasOurs[0] === want)) || (!want && hasOurs.length)) {
        await bbJson('cart/update.js', { discount: want });
        changed = true;
      }
    }
    if (/\/cart\/?$/.test(location.pathname)) {
      try {
        const msg = sessionStorage.getItem('rgsBundleCouponNote');
        if (msg) { notes.unshift(msg); if (!changed) sessionStorage.removeItem('rgsBundleCouponNote'); }
      } catch { /* storage off */ }
    }

    this._note(pend, notes);
    if (changed && /\/cart\/?$/.test(location.pathname)) {
      const last = +sessionStorage.getItem('rgsBundleReload') || 0;
      if (Date.now() - last > 8000) { sessionStorage.setItem('rgsBundleReload', String(Date.now())); location.reload(); }
    }
  },
  _note(pend, notes) {
    document.querySelector('.rgs-bb-note')?.remove();
    const msgs = [];
    for (const b of pend) {
      const done = b.mats.filter((m) => m.done).length;
      const next = b.mats.find((m) => !m.done);
      const here = next && location.pathname.replace(/\/$/, '') === new URL(next.url, location.origin).pathname.replace(/\/$/, '');
      msgs.push(here
        ? `Designing mat ${done + 1} of ${b.mats.length} for your ${bbEsc(b.name)} bundle. Add it to your cart when it's ready.`
        : `Your ${bbEsc(b.name)} bundle: ${done} of ${b.mats.length} mats designed. <a href="${bbEsc(next.url)}">Design mat ${done + 1}</a>`);
    }
    if (/\/cart\/?$/.test(location.pathname)) msgs.push(...notes);
    if (!msgs.length) return;
    const el = document.createElement('div');
    el.className = 'rgs-bb-note';
    el.setAttribute('role', 'status');
    el.innerHTML = msgs.map((m) => `<p>${m}</p>`).join('');
    (document.querySelector('main') || document.body).prepend(el);
  },
};
window.RgsBundleGuard = BundleGuard;

// Show the hidden bundle codes as "Bundle discount" in the cart and cart drawer (Horizon prints the code
// itself in <bdi> tags and aria-labels). The code stays on the remove button's data attribute so removing
// still works. Checkout is Shopify's own page and still shows the code there.
const BB_CODE_RE = /RGSB(?:UNDLE\d+)?-[A-Z0-9-]+/gi;
const bbMask = (scope) => {
  scope.querySelectorAll?.('bdi').forEach((el) => {
    if (BB_CODE_RE.test(el.textContent)) el.textContent = el.textContent.replace(BB_CODE_RE, 'Bundle discount');
    BB_CODE_RE.lastIndex = 0;
  });
  scope.querySelectorAll?.('[aria-label*="RGSB" i]').forEach((el) => {
    el.setAttribute('aria-label', el.getAttribute('aria-label').replace(BB_CODE_RE, 'bundle discount'));
  });
};
bbMask(document);
// After later page changes, run the full mask again only when a <bdi> actually shows a bundle code.
// The code always appears in a <bdi> (Horizon's discount pill and line discounts) next to the aria-labels
// that name it, so pages without one skip the full-page attribute search on every change.
const bdis = document.getElementsByTagName('bdi');
const bbHasCode = () => {
  for (let i = 0; i < bdis.length; i++) {
    if (/RGSB/i.test(bdis[i].textContent)) return true;
  }
  return false;
};
let bbMaskQueued = false;
new MutationObserver(() => {
  if (bbMaskQueued) return;
  bbMaskQueued = true;
  requestAnimationFrame(() => { bbMaskQueued = false; if (bbHasCode()) bbMask(document); });
}).observe(document.documentElement, { childList: true, subtree: true });
BundleGuard.run();
document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  Promise.resolve(e.promise).catch(() => {}).finally(() => setTimeout(() => BundleGuard.run(), 300));
});

/* ---------------- Volume code guard ----------------
   Codes such as LEGION20OFF (new customers) can't be used once the cart holds the volume discount's
   minimum number of mats: the automatic "Buy More, Save More" discount applies instead. Shopify codes
   have no maximum-quantity rule, so on every page and after every cart change this removes a blocked
   code from the cart (keeping any other codes) and shows a short note in the cart drawer and cart page.
   Settings: Theme settings > Rubicon style (snippets/rgs-theme-options.liquid prints them as JSON).
   It shares the bundle guard's queue, so the two never update the cart at the same time; it only ever
   removes codes and only posts when a blocked code is actually present, so it can't loop. */
try {
  const vgCfg = (() => {
    try { return JSON.parse(document.getElementById('rgs-vguard-config')?.textContent || 'null'); } catch { return null; }
  })();
  const vgList = (s) => String(s || '').split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
  const vgCodes = vgList(vgCfg?.codes);
  const vgTypes = vgList(vgCfg?.types);
  const vgMin = Number(vgCfg?.min) || 0;
  const VG_NOTE = 'rgsVolumeNote';
  const vgOwn = new WeakSet(); // events this guard dispatched, so it doesn't react to its own refresh

  if (vgCodes.length && vgTypes.length && vgMin > 0) {
    const VolumeGuard = {
      note: '',
      made: false, // a note element was added at some point (only then can a stale one need removing)
      run() {
        // Same queue as the bundle guard: a run waits for any bundle guard run and the other way round.
        BundleGuard.running = (BundleGuard.running || Promise.resolve()).then(() => this._run()).catch((e) => console.warn('[rgs-volume]', e));
        return BundleGuard.running;
      },
      async _run() {
        const cart = await bbJson('cart.js');
        const mats = (cart.items || []).reduce((n, l) => n + (vgTypes.includes(String(l.product_type || '').trim().toLowerCase()) ? l.quantity : 0), 0);
        const codes = (cart.discount_codes || []).map((d) => String(d.code || '')).filter(Boolean);
        const blocked = codes.filter((c) => vgCodes.includes(c.toLowerCase()));
        let shown = '';
        try { shown = sessionStorage.getItem(VG_NOTE) || ''; } catch { /* storage off */ }
        if (mats < vgMin) {
          // Below the minimum the code is fine again; drop the note.
          if (shown) try { sessionStorage.removeItem(VG_NOTE); } catch { /* storage off */ }
          this.show('');
          return;
        }
        if (blocked.length) {
          const keep = codes.filter((c) => !vgCodes.includes(c.toLowerCase()));
          const updated = await bbJson('cart/update.js', { discount: keep.join(',') });
          shown = blocked[0].toUpperCase();
          try { sessionStorage.setItem(VG_NOTE, shown); } catch { /* storage off */ }
          // Refresh Horizon's cart drawer and cart page the way an outside discount change does.
          try {
            const deferred = CartDiscountUpdateEvent.createPromise();
            const ev = new CartDiscountUpdateEvent({ discountCodes: keep.map((code) => ({ code })), promise: deferred.promise });
            vgOwn.add(ev);
            document.dispatchEvent(ev);
            deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(updated), detail: { source: 'rgs-volume-guard' } });
          } catch (e) { console.warn('[rgs-volume] refresh', e); }
        }
        this.show(shown ? String(vgCfg.note || '').replace(/\[code\]/gi, shown).replace(/\[min\]/gi, String(vgMin)) : '');
      },
      // Keep the note in the drawer summary and the cart page summary (Horizon re-renders both).
      show(msg) {
        this.note = msg;
        const hosts = [
          ...document.querySelectorAll('#cart-drawer .cart-drawer__summary'),
          ...[...document.querySelectorAll('.cart-page__summary')].map((s) => s.querySelector('.cart-summary__inner') || s),
        ];
        hosts.forEach((host) => {
          let el = host.querySelector(':scope > .rgs-vg-note');
          if (!msg) { el?.remove(); return; }
          if (!el) {
            this.made = true;
            el = document.createElement('div');
            el.className = 'rgs-vg-note';
            el.setAttribute('role', 'status');
            el.appendChild(document.createElement('p'));
            const ship = host.querySelector(':scope > .rgs-ship');
            if (ship) ship.after(el); else host.prepend(el);
          }
          const p = el.querySelector('p');
          if (p.textContent !== msg) p.textContent = msg;
        });
      },
    };
    window.RgsVolumeGuard = VolumeGuard;
    let vgQueued = false;
    new MutationObserver(() => {
      if (vgQueued) return;
      vgQueued = true;
      requestAnimationFrame(() => { vgQueued = false; if (VolumeGuard.note || (VolumeGuard.made && document.querySelector('.rgs-vg-note'))) VolumeGuard.show(VolumeGuard.note); });
    }).observe(document.body, { childList: true, subtree: true });
    // First check: an empty cart has no mats, so there is nothing to remove; just clear an old note
    // (what the check would do) without fetching /cart.js. Otherwise run once the page is idle,
    // straight away on the cart page, where the note shows.
    if (vgCfg.items === 0) {
      try { sessionStorage.removeItem(VG_NOTE); } catch { /* storage off */ }
    } else if (/\/cart\/?$/.test(location.pathname) || !('requestIdleCallback' in window)) {
      VolumeGuard.run();
    } else {
      requestIdleCallback(() => VolumeGuard.run(), { timeout: 3000 });
    }
    const vgOnCart = (e) => {
      if (vgOwn.has(e)) return;
      Promise.resolve(e.promise).catch(() => {}).finally(() => setTimeout(() => VolumeGuard.run(), 300));
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, vgOnCart);
    document.addEventListener(StandardEvents.cartDiscountUpdate, vgOnCart);
  }
} catch (e) { console.warn('[rgs-volume]', e); }

/* ---------------- Free gift guard ----------------
   "Buy a mat, get a tube": while the promo runs (Theme settings > Rubicon style > Free gift with purchase,
   printed by snippets/rgs-theme-options.liquid as #rgs-gift-config), a cart holding a line of one of the
   set product types gets one gift variant added with the hidden property _rgs_gift. Shopify's automatic
   discounts decide whether the cart qualifies: if the added line doesn't come back free, it is removed
   again and this cart's contents (lines and codes) are remembered, so it is tried again only once they
   change. The guard only adds, trims or removes its own _rgs_gift line: a tube the customer added
   themselves is never touched (with one in the cart nothing is added, and our line makes way for it).
   A gift the customer removed stays removed for that cart until it is emptied or the session ends.
   The gift combines only with the codes in "Codes the gift combines with" (gfCfg.allow, default LEGION20OFF):
   with a bundle line (_bundle) in the cart, any other applied code, or any other discount on a line (the
   volume discount, the add-on discount) it is never added and ours is removed.
   The guard never adds or removes discount codes.
   It shares the bundle guard's queue, so the three guards never change the cart at the same time. */
try {
  const gfCfg = (() => {
    try { return JSON.parse(document.getElementById('rgs-gift-config')?.textContent || 'null'); } catch { return null; }
  })();
  const gfVariant = Number(gfCfg?.variant) || 0;
  const gfProduct = Number(gfCfg?.product) || 0;
  const gfTypes = String(gfCfg?.types || '').split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
  // Codes the gift combines with; a missing or unset setting falls back to LEGION20OFF (empty text allows none).
  const gfAllow = (gfCfg && gfCfg.allow == null ? 'LEGION20OFF' : String(gfCfg?.allow ?? ''))
    .split(',').map((v) => v.trim().toUpperCase()).filter(Boolean);
  // "YYYY-MM-DD HH:MM" in store time (offset "+HHMM"/"-HHMM" from Liquid) to a timestamp; null = no limit.
  const gfTime = (s, z) => {
    const m = String(s || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    if (!m) return null;
    const zm = String(z || '').match(/^([+-])(\d{2}):?(\d{2})$/);
    const off = zm ? (zm[1] === '-' ? -1 : 1) * (+zm[2] * 60 + +zm[3]) : 0;
    const t = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) - off * 60000;
    return Number.isFinite(t) ? t : null;
  };
  const gfStart = gfTime(gfCfg?.start, gfCfg?.offset);
  const gfEnd = gfTime(gfCfg?.end, gfCfg?.offset);
  const gfActive = () => {
    const now = Date.now();
    return (gfStart === null || now >= gfStart) && (gfEnd === null || now < gfEnd);
  };
  const GF_STATE = 'rgsGiftState';
  const GF_MAX_ADDS = 3; // adds per page load, so nothing can loop
  const gfOwn = new WeakSet(); // events this guard dispatched
  const gfOurs = (l) => !!(l?.properties && l.properties._rgs_gift);
  const gfIsGift = (l) => Number(l?.variant_id) === gfVariant || (gfProduct && Number(l?.product_id) === gfProduct);
  // A line counts as free when nothing is left to pay on it (any partial discount, such as a typed code, isn't the gift).
  const gfFree = (l) => {
    const price = Number(l.original_line_price ?? l.line_price ?? 0);
    const final = Number(l.final_line_price ?? price);
    const alloc = (l.line_level_discount_allocations || []).reduce((n, a) => n + (Number(a.amount) || 0), 0);
    const off = Math.max(Number(l.total_discount) || 0, alloc, (l.discounts || []).reduce((n, d) => n + (Number(d.amount) || 0), 0));
    return final <= 0 || (price > 0 && off >= price);
  };
  // The cart's contents apart from our gift, so a cart that didn't qualify is tried again only after a change.
  const gfSig = (cart) => (cart.items || []).filter((l) => !gfOurs(l)).map((l) => `${l.variant_id}x${l.quantity}`).sort().join(',')
    + '|' + (cart.discount_codes || []).map((d) => String(d.code || '').toUpperCase()).sort().join(',');
  const gfRead = () => { try { return JSON.parse(sessionStorage.getItem(GF_STATE)) || null; } catch { return null; } };
  const gfWrite = (s) => { try { if (s) sessionStorage.setItem(GF_STATE, JSON.stringify(s)); else sessionStorage.removeItem(GF_STATE); } catch { /* storage off */ } };

  if (gfCfg && gfVariant && gfTypes.length && !window.Shopify?.designMode) {
    const GiftGuard = {
      note: '',
      made: false,
      adds: 0,
      timer: 0,
      // Debounced: several cart events in a row run the guard once.
      schedule(ms = 300) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.run(), ms);
      },
      run() {
        // Same queue as the bundle and volume guards.
        BundleGuard.running = (BundleGuard.running || Promise.resolve()).then(() => this._run()).catch((e) => console.warn('[rgs-gift]', e));
        return BundleGuard.running;
      },
      async _run() {
        let cart = await bbJson('cart.js');
        if (!(cart.items || []).length) { gfWrite(null); this.show(''); return; }
        const tok = String(cart.token || '');
        let st = gfRead();
        if (!st || st.tok !== tok) st = { tok, had: false, removed: false, tried: [] };
        if (!Array.isArray(st.tried)) st.tried = [];
        let touched = null; // the last line changed, for Horizon's refresh event

        const change = async (line, quantity) => {
          cart = await bbJson('cart/change.js', { id: line.key, quantity });
          touched = { id: String(line.key), quantity };
        };
        const qualifying = () => (cart.items || []).filter((l) => !gfOurs(l) && !gfIsGift(l) && gfTypes.includes(String(l.product_type || '').trim().toLowerCase()));
        const ours = () => (cart.items || []).filter(gfOurs);
        const theirs = () => (cart.items || []).filter((l) => !gfOurs(l) && gfIsGift(l));
        const active = gfActive();
        // The gift combines only with the allowed codes (gfAllow): with a bundle line (builder or product type Bundle),
        // any other applied code, or a discount on another line that isn't an allowed code (the volume discount, the
        // add-on discount, an unknown source), nothing is added and ours goes.
        const allowedApp = (d) => !!d && d.type === 'discount_code'
          && (gfAllow.includes(String(d.key || '').trim().toUpperCase()) || gfAllow.includes(String(d.title || '').trim().toUpperCase()));
        const lineOff = (l) => {
          const allocs = (l.line_level_discount_allocations || []).filter((a) => Number(a.amount) > 0);
          if (allocs.length) return allocs.some((a) => !allowedApp(a.discount_application));
          return Number(l.total_discount) > 0; // a discount with no known source
        };
        const bundled = (cart.items || []).some((l) => (l.properties && l.properties._bundle) || String(l.product_type || '').trim().toLowerCase() === 'bundle')
          || (cart.discount_codes || []).some((d) => d && d.applicable !== false && !gfAllow.includes(String(d.code || '').trim().toUpperCase()))
          || (cart.items || []).some((l) => !gfOurs(l) && lineOff(l));

        let mine = ours();
        if (mine.length) {
          st.had = true;
          if (!active || bundled || !qualifying().length || theirs().length) {
            // Promo over, a bundle in the cart, no mat left, or the customer added a tube themselves (that one gets the discount).
            for (const l of mine) await change(l, 0);
            st.had = false;
          } else {
            // Keep exactly one line of quantity 1.
            for (const l of mine.slice(1)) await change(l, 0);
            mine = ours();
            if (mine[0] && mine[0].quantity > 1) await change(mine[0], 1);
            mine = ours();
            if (mine[0] && !gfFree(mine[0])) {
              // Shopify's discounts no longer make it free (another discount won, or the cart changed).
              await change(mine[0], 0);
              st.had = false;
              st.tried = [...st.tried, gfSig(cart)].slice(-20);
            }
          }
        } else {
          // It was here and is gone while mats remain: the customer removed it, so leave it out for this cart.
          if (st.had && qualifying().length) st.removed = true;
          st.had = false;
          const sig = gfSig(cart);
          if (active && !bundled && qualifying().length && !theirs().length && !st.removed && !st.tried.includes(sig) && this.adds < GF_MAX_ADDS) {
            this.adds++;
            try {
              await bbJson('cart/add.js', { items: [{ id: gfVariant, quantity: 1, properties: { _rgs_gift: '1' } }] });
              cart = await bbJson('cart.js');
              const added = ours()[0];
              if (added) {
                touched = { id: String(added.key), quantity: added.quantity };
                if (gfFree(added)) st.had = true;
                else {
                  // The cart doesn't qualify: take it out again and don't retry these contents.
                  await change(added, 0);
                  st.tried = [...st.tried, sig].slice(-20);
                }
              } else {
                st.tried = [...st.tried, sig].slice(-20);
              }
            } catch (e) {
              // Sold out or another cart error: don't retry these contents.
              st.tried = [...st.tried, sig].slice(-20);
              console.warn('[rgs-gift] add', e);
            }
          }
        }
        gfWrite(st);
        if (touched) {
          // Refresh Horizon's cart drawer, cart page and cart count (no sections attached, so they re-render).
          try {
            if (!touched.quantity) cart = await bbJson('cart.js');
            const deferred = CartLinesUpdateEvent.createPromise();
            const ev = new CartLinesUpdateEvent({
              action: touched.quantity ? 'update' : 'remove', context: 'standard-action',
              lines: [touched], promise: deferred.promise,
            });
            gfOwn.add(ev);
            document.dispatchEvent(ev);
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { items: cart.items, itemCount: cart.item_count, source: 'rgs-gift-guard' },
            });
          } catch (e) { console.warn('[rgs-gift] refresh', e); }
        }
        const present = ours().some(gfFree);
        this.show(present ? String(gfCfg.note || '') : '');
      },
      // Same note markup and places as the volume code guard (drawer summary and cart page summary).
      show(msg) {
        this.note = msg;
        const hosts = [
          ...document.querySelectorAll('#cart-drawer .cart-drawer__summary'),
          ...[...document.querySelectorAll('.cart-page__summary')].map((s) => s.querySelector('.cart-summary__inner') || s),
        ];
        hosts.forEach((host) => {
          let el = host.querySelector(':scope > .rgs-gift-note');
          if (!msg) { el?.remove(); return; }
          if (!el) {
            this.made = true;
            el = document.createElement('div');
            el.className = 'rgs-gift-note';
            el.setAttribute('role', 'status');
            el.appendChild(document.createElement('p'));
            const after = host.querySelector(':scope > .rgs-vg-note') || host.querySelector(':scope > .rgs-ship');
            if (after) after.after(el); else host.prepend(el);
          }
          const p = el.querySelector('p');
          if (p.textContent !== msg) p.textContent = msg;
        });
      },
    };
    window.RgsGiftGuard = GiftGuard;
    let gfQueued = false;
    new MutationObserver(() => {
      if (gfQueued) return;
      gfQueued = true;
      requestAnimationFrame(() => { gfQueued = false; if (GiftGuard.note || (GiftGuard.made && document.querySelector('.rgs-gift-note'))) GiftGuard.show(GiftGuard.note); });
    }).observe(document.body, { childList: true, subtree: true });
    // First check: an empty cart has nothing to add a gift to; just forget the old cart's state.
    if (gfCfg.items === 0) gfWrite(null);
    else if (/\/cart\/?$/.test(location.pathname) || !('requestIdleCallback' in window)) GiftGuard.schedule(0);
    else requestIdleCallback(() => GiftGuard.schedule(0), { timeout: 3000 });
    const gfOnCart = (e) => {
      if (gfOwn.has(e)) return;
      Promise.resolve(e.promise).catch(() => {}).finally(() => GiftGuard.schedule(400));
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, gfOnCart);
    document.addEventListener(StandardEvents.cartDiscountUpdate, gfOnCart);
  }
} catch (e) { console.warn('[rgs-gift]', e); }

// Line icons for the tier cards, in the style of snippets/rgs-icon (1.8 stroke, currentColor).
const BB_ICONS = {
  mat: '<svg viewBox="0 0 44 28" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="2" y="3" width="40" height="22" rx="3.5"/><rect x="6" y="7" width="32" height="14" rx="1.5" stroke-dasharray="2.4 2.4" stroke-width="1.4"/></svg>',
  sleeves: '<svg viewBox="0 0 44 32" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><g fill="var(--bb-icon-fill, #fff)"><rect x="16" y="3" width="12" height="18" rx="2" transform="rotate(-30 22 30)"/><rect x="16" y="3" width="12" height="18" rx="2" transform="rotate(30 22 30)"/><rect x="16" y="3" width="12" height="18" rx="2" transform="rotate(0 22 30)"/></g></svg>',
  tube: '<svg viewBox="0 0 44 28" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M7 5h6l3.5 9-3.5 9H7l-3.5-9z"/><path d="M13 5h25l3.5 9-3.5 9H13"/><path d="M16.5 14H41.5" stroke-dasharray="2.4 2.4" stroke-width="1.4"/></svg>',
};

// Swatch colors for sleeve and ring variants, matched to the product photos. Unknown names fall back to grey.
const BB_SWATCH = {
  black: '#141414', white: '#ececec', crimson: '#9b1f1f', coral: '#d9605c', cobalt: '#2a56c6', 'ice blue': '#6fd3dc', frost: '#6fd3dc',
  forest: '#0d3b24', mint: '#9fe0c6', lime: '#a8d77a', purple: '#4b2470', lavender: '#b3a2e6', yellow: '#ead23a',
  orange: '#d4561b', pink: '#e4a68f', silver: '#a9a9ad', bronze: '#a77a35',
  'dark blue': '#1d3557', 'light blue': '#8ecae6', gray: '#8a8f94', grey: '#8a8f94',
  red: '#c4262e', blue: '#2a56c6', green: '#2e7d32', gold: '#c9a227', brown: '#6b4423', teal: '#1f8a8a',
};

class RgsBundleBuilder extends HTMLElement {
  // A staged wizard: only the current step is open, finished steps collapse to a one-line summary with
  // an Edit link, later steps stay hidden. Nothing is pre-selected, so the summary starts empty.
  connectedCallback() {
    if (this.cfg) return;
    try { this.cfg = JSON.parse(this.querySelector('[data-bb-config]').textContent); } catch (e) { console.warn('[rgs-bundle] bad config', e); return; }
    this.cur = this.dataset.currency;
    const c = this.cfg;
    c.ready = (c.ready || []).map((p) => ({ ...p, variants: p.variants.filter((v) => v.ok && !/custom artwork/i.test(v.title)) })).filter((p) => p.variants.length);
    c.custom = (c.custom || []).map((p) => ({ ...p, variants: p.variants.filter((v) => v.ok) })).filter((p) => p.variants.length);
    this.keys = ['tier', 'mats', ...(c.sleeves ? ['sleeves'] : []), ...(c.ring || c.tube ? ['storage'] : []), 'review'];
    this.s = {
      step: 0, cheer: null, tier: null, type: null, layout: 'single',
      sameMat: true, mats: [], sleevesOn: null, sameSleeve: true, sleeves: [], storageOn: null, sameStorage: true, storage: [],
    };
    this.steps = this.querySelector('[data-bb-steps]');
    this.summary = this.querySelector('[data-bb-summary]');
    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-bb-add]')) return this.add();
      const next = e.target.closest('[data-bb-next]');
      if (next && this.valid(this.keys[this.s.step])) return this.go(this.s.step + 1, true);
      const edit = e.target.closest('[data-bb-edit]');
      if (edit) this.go(+edit.dataset.bbEdit, false);
    });
    // Links from the homepage spotlight open the builder with a tier chosen: ?tier=duel
    const want = new URLSearchParams(location.search).get('tier');
    const ti = c.tiers.findIndex((x) => x.key === want);
    if (ti >= 0) {
      this.s.tier = ti;
      this.sync();
      this.s.step = 1;
      this.s.cheer = this.cheerFor('mats');
    }
    this.render();
  }

  get tier() { return this.s.tier == null ? null : this.cfg.tiers[this.s.tier]; }
  get two() { return this.s.type === 'custom' && this.s.layout === 'two' && this.tier?.players > 1 && this.twoProduct; }
  get twoProduct() { return this.cfg.custom.find((p) => p.seats === 2); }
  get matPool() { return this.two ? [this.twoProduct] : this.s.type === 'custom' ? this.cfg.custom.filter((p) => p.seats === 1) : this.cfg.ready; }
  get matCount() { return this.two ? this.tier.players / 2 : this.tier.players; }
  get roman() { return ['I', 'II', 'III', 'IV', 'V']; }

  // Keep the per-mat, per-pack and per-player arrays the right length, copying the first slot when "same" is on.
  sync() {
    const s = this.s, c = this.cfg;
    if (!this.tier) return;
    const n = this.matCount, players = this.tier.players, pool = this.matPool;
    s.mats = Array.from({ length: n }, (_, i) => {
      const m = s.sameMat ? s.mats[0] : s.mats[i] ?? s.mats[0];
      return m && pool.some((p) => p.id === m.pid) ? { ...m } : { pid: null, vid: null };
    });
    s.sleeves = Array.from({ length: players }, (_, i) => (s.sameSleeve ? s.sleeves[0] : s.sleeves[i] ?? s.sleeves[0]) ?? null);
    s.storage = Array.from({ length: players }, (_, i) => {
      const src = { ...((s.sameStorage ? s.storage[0] : s.storage[i] ?? s.storage[0]) || { tube: null, vid: null }) };
      if (!c.ring) src.tube = true;
      if (!c.tube) src.tube = false;
      return src;
    });
  }

  // Whether a step's choices are complete.
  valid(key) {
    const s = this.s;
    if (key === 'tier') return s.tier != null;
    if (key === 'mats') return !!s.type && s.mats.length > 0 && s.mats.every((m) => m.vid);
    if (key === 'sleeves') return s.sleevesOn === false || (s.sleevesOn === true && s.sleeves.every(Boolean));
    if (key === 'storage') return s.storageOn === false || (s.storageOn === true && s.storage.every((x) => x.tube === true || (x.tube === false && x.vid)));
    return true;
  }

  cheerFor(key) {
    const t = this.tier, custom = this.s.type === 'custom';
    return {
      mats: `Great! ${t.name} it is. Now choose your mats.`,
      sleeves: `Looking good. Now pick your sleeves.`,
      storage: `Nice. Now choose a storage ring or tube to keep ${t.players > 1 ? 'every mat' : 'your mat'} safe.`,
      review: custom ? `Your bundle is ready. Check the summary, then continue to design your mats.` : `Your bundle is ready. Check the summary, then add it to your cart.`,
    }[key] || null;
  }

  go(step, cheer) {
    this.s.step = Math.max(0, Math.min(step, this.keys.length - 1));
    this.s.cheer = cheer ? this.cheerFor(this.keys[this.s.step]) : null;
    this.render();
    const el = this.steps.querySelector('.rgs-bb__step.is-active');
    if (el) {
      el.querySelector('.rgs-bb__legend')?.focus({ preventScroll: true });
      const top = el.getBoundingClientRect().top;
      if (top < 80 || top > innerHeight * 0.6) el.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  onChange(e) {
    const t = e.target, s = this.s, k = t.dataset.k, i = +t.dataset.i || 0;
    if (!k) return;
    const val = t.type === 'checkbox' ? t.checked : t.value;
    if (k === 'tier') {
      s.tier = +val;
      this.sync();
      return this.go(1, true);
    }
    if (k === 'type') { s.type = val; s.layout = 'single'; s.mats = []; }
    else if (k === 'layout') { s.layout = val; s.mats = []; }
    else if (k === 'pid') {
      const p = this.matPool.find((x) => x.id === +val);
      s.mats[i] = p ? { pid: p.id, vid: p.variants[0].id } : { pid: null, vid: null };
    } else if (k === 'opt') {
      // One dropdown per option: keep the other choices where a variant allows it.
      const j = +t.dataset.j, p = this.matPool.find((x) => x.id === s.mats[i].pid);
      const cur = p.variants.find((v) => v.id === s.mats[i].vid)?.o || [];
      const want = cur.map((x, n) => (n === j ? val : x));
      const score = (v) => v.o.reduce((a, x, n) => a + (x === want[n] ? 1 : 0), 0);
      const best = p.variants.filter((v) => v.o[j] === val).sort((a, b) => score(b) - score(a))[0];
      if (best) s.mats[i].vid = best.id;
    } else if (k === 'sleevesOn' || k === 'storageOn') s[k] = val === '1';
    else if (k === 'sleeve') s.sleeves[i] = val ? +val : null;
    else if (k === 'ring') s.storage[i].vid = val ? +val : null;
    else if (k === 'tube') s.storage[i].tube = val === '1';
    else s[k] = val; // sameMat, sameSleeve, sameStorage
    if (s.sameMat && (k === 'pid' || k === 'opt')) s.mats = s.mats.map(() => ({ ...s.mats[0] }));
    if (s.sameSleeve && k === 'sleeve') s.sleeves = s.sleeves.map(() => s.sleeves[0]);
    if (s.sameStorage && (k === 'ring' || k === 'tube')) s.storage = s.storage.map(() => ({ ...s.storage[0] }));
    this.sync();
    const id = t.id;
    this.render();
    if (id) document.getElementById(id)?.focus();
  }

  opts(list, sel) { return list.map((o) => `<option value="${bbEsc(o.v)}"${o.v === sel ? ' selected' : ''}>${bbEsc(o.l)}</option>`).join(''); }
  vlabel(p, v) { return v.title === 'Default Title' ? p.title : v.title; }

  // One-line description of a finished step.
  doneText(key) {
    const s = this.s, ls = this.lines();
    const list = (role) => ls.filter((l) => l.role === role).map((l) => `${l.qty} × ${l.label}`).join(', ');
    if (key === 'tier') return `${this.tier.name}, ${this.tier.players} player${this.tier.players > 1 ? 's' : ''}`;
    if (key === 'mats') return `${s.type === 'custom' ? 'Custom' : 'Ready-made'}: ${list('mat')}`;
    if (key === 'sleeves') return s.sleevesOn ? list('sleeves') : 'No sleeves';
    if (key === 'storage') return s.storageOn ? list('storage') : 'No storage';
    return '';
  }

  render() {
    const c = this.cfg, s = this.s, u = this.dataset.uid || (this.dataset.uid = Math.random().toString(36).slice(2, 7));
    const radio = (k, v, checked, label, sub, after = '', extraClass = '') => `
      <label class="rgs-bb__card${extraClass}${checked ? ' is-on' : ''}">
        <input type="radio" name="${u}-${k}" id="${u}-${k}-${v}" value="${v}" data-k="${k}"${checked ? ' checked' : ''}>
        <span class="rgs-bb__card-title">${bbEsc(label)}</span>${sub ? `<span class="rgs-bb__card-text">${bbEsc(sub)}</span>` : ''}${after}
      </label>`;
    // What's in each tier, drawn: mats, sleeve packs and storage, each with a count.
    const kit = (n) => `<span class="rgs-bb__kit" aria-hidden="true">${[['mat', BB_ICONS.mat], ['sleeves', BB_ICONS.sleeves], ['tube', BB_ICONS.tube]]
      .map(([name, svg]) => `<span class="rgs-bb__kit-item rgs-bb__kit-item--${name}">${svg}<b>×${n}</b></span>`).join('')}</span>`;
    const tog = (k, on, label) => `<label class="rgs-bb__tog"><input type="checkbox" id="${u}-${k}" data-k="${k}"${on ? ' checked' : ''}><span class="rgs-bb__box" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-7"/></svg></span><span>${bbEsc(label)}</span></label>`;
    // A color picker as swatch radios: chip, name, sold-out ones crossed out and disabled.
    const swatches = (p, sel, k, i, label) => `<fieldset class="rgs-bb__swatches"><legend class="rgs-bb__swatch-legend">${bbEsc(label)}${sel ? `: <b>${bbEsc(this.vlabel(p, p.variants.find((v) => v.id === sel) || {}))}</b>` : ''}</legend><div class="rgs-bb__swatch-grid">${p.variants.map((v) => {
      const name = this.vlabel(p, v), on = v.id === sel;
      return `<label class="rgs-bb__swatch${on ? ' is-on' : ''}${v.ok ? '' : ' is-out'}" title="${bbEsc(name)}${v.ok ? '' : ' (Out of stock)'}"><input type="radio" name="${u}-${k}-${i}" id="${u}-${k}-${i}-${v.id}" value="${v.id}" data-k="${k}" data-i="${i}"${on ? ' checked' : ''}${v.ok ? '' : ' disabled'}><span class="rgs-bb__chip" style="--c: ${BB_SWATCH[name.toLowerCase()] || '#c6c6c6'}" aria-hidden="true"></span><span class="rgs-bb__swatch-name">${bbEsc(name)}${v.ok ? '' : '<small>Out of stock</small>'}</span></label>`;
    }).join('')}</div></fieldset>`;
    const next = (key, label = 'Continue') => `<div class="rgs-bb__nav"><button type="button" class="rgs-btn rgs-btn--primary rgs-bb__next" data-bb-next${this.valid(key) ? '' : ' disabled'}>${bbEsc(label)}</button>${this.valid(key) ? '' : `<span class="rgs-bb__nav-hint">${bbEsc(this.missing(key))}</span>`}</div>`;
    const titles = { tier: 'Choose your bundle', mats: 'Choose your mats', sleeves: 'Sleeves', storage: 'Storage', review: 'Review your bundle' };
    let h = '';

    this.keys.forEach((key, idx) => {
      if (idx > s.step) return;
      const num = this.roman[idx] || idx + 1;
      if (idx < s.step) {
        h += `<div class="rgs-bb__step rgs-bb__step--done"><span class="rgs-bb__num" aria-hidden="true">${num}</span><div class="rgs-bb__done"><p class="rgs-bb__done-title">${titles[key]}</p><p class="rgs-bb__done-val">${bbEsc(this.doneText(key))}</p></div><button type="button" class="rgs-bb__edit" data-bb-edit="${idx}" aria-label="Edit ${titles[key]}">Edit</button></div>`;
        return;
      }
      let badge = '';
      if (key === 'sleeves') badge = `<span class="rgs-bb__save">${c.pct.part}% off, ${c.pct.full}% with storage</span>`;
      if (key === 'storage') badge = `<span class="rgs-bb__save">${c.pct.part}% off, ${c.pct.full}% with sleeves</span>`;
      h += `<fieldset class="rgs-bb__step is-active">`;
      if (s.cheer) h += `<p class="rgs-bb__cheer" role="status">${bbEsc(s.cheer)}</p>`;
      h += `<legend class="rgs-bb__legend" tabindex="-1"><span class="rgs-bb__num">${num}</span> ${titles[key]} ${badge}</legend>`;

      if (key === 'tier') {
        h += `<div class="rgs-bb__cards rgs-bb__cards--3">`;
        c.tiers.forEach((t, i) => { h += radio('tier', i, i === s.tier, t.name, t.text, kit(t.players)); });
        h += `</div>`;
      }

      if (key === 'mats') {
        h += `<div class="rgs-bb__cards">`;
        if (c.custom.length) h += radio('type', 'custom', s.type === 'custom', 'Custom mats', 'Your own artwork, designed in Playmat Studio');
        if (c.ready.length) h += radio('type', 'ready', s.type === 'ready', 'Ready-made mats', 'Pick from our game mats');
        h += `</div>`;
        if (s.type) {
          if (s.type === 'custom' && this.tier.players > 1 && this.twoProduct) {
            h += `<div class="rgs-bb__cards rgs-bb__cards--sub">`;
            h += radio('layout', 'single', s.layout !== 'two', `${this.tier.players} single mats`, 'One mat per player');
            h += radio('layout', 'two', s.layout === 'two', `${this.tier.players / 2} two-player mat${this.tier.players > 2 ? 's' : ''}`, this.twoProduct.title);
            h += `</div>`;
          }
          if (this.matCount > 1) h += tog('sameMat', s.sameMat, 'Same mat for every player');
          const pool = this.matPool;
          (s.sameMat ? [s.mats[0]] : s.mats).forEach((m, i) => {
            const p = pool.find((x) => x.id === m.pid) || (pool.length === 1 ? pool[0] : null);
            if (p && !m.pid) { s.mats.forEach((x, n) => { if (s.sameMat || n === i) { x.pid = p.id; x.vid = p.variants[0].id; } }); }
            h += `<div class="rgs-bb__row">`;
            if (p?.img) h += `<img class="rgs-bb__thumb" src="${bbEsc(p.img)}" alt="" width="64" height="64" loading="lazy">`;
            h += `<div class="rgs-bb__fields">`;
            if (!s.sameMat) h += `<p class="rgs-bb__slot">Mat ${i + 1}</p>`;
            if (pool.length > 1) h += `<label class="rgs-bb__field"><span>${s.type === 'custom' ? 'Size' : 'Mat'}</span><select id="${u}-pid-${i}" data-k="pid" data-i="${i}"><option value="">${s.type === 'custom' ? 'Choose a size' : 'Choose a mat'}</option>${this.opts(pool.map((x) => ({ v: x.id, l: x.title })), p?.id)}</select></label>`;
            else if (p) h += `<p class="rgs-bb__fixed">${bbEsc(p.title)}</p>`;
            if (p && p.variants.length > 1) {
              const cur = p.variants.find((v) => v.id === (s.sameMat ? s.mats[0] : m).vid) || p.variants[0];
              p.options.forEach((name, j) => {
                const vals = [...new Set(p.variants.map((v) => v.o?.[j]).filter(Boolean))];
                if (vals.length < 2 && p.options.length > 1) return; // nothing to choose
                h += `<label class="rgs-bb__field"><span>${bbEsc(name)}</span><select id="${u}-opt-${i}-${j}" data-k="opt" data-i="${i}" data-j="${j}">${this.opts(vals.map((x) => ({ v: x, l: x })), cur.o?.[j])}</select></label>`;
              });
            }
            h += `</div></div>`;
          });
        }
        h += next('mats');
      }

      if (key === 'sleeves') {
        const n = this.tier.players;
        h += `<div class="rgs-bb__cards">`;
        h += radio('sleevesOn', '1', s.sleevesOn === true, 'Add sleeves', `${n} × ${c.sleeves.title}, one pack per player`, '', ' rgs-bb__card--sm');
        h += radio('sleevesOn', '0', s.sleevesOn === false, 'No sleeves', 'Skip this step', '', ' rgs-bb__card--sm');
        h += `</div>`;
        if (s.sleevesOn) {
          if (n > 1) h += tog('sameSleeve', s.sameSleeve, 'Same color for every pack');
          (s.sameSleeve ? [s.sleeves[0]] : s.sleeves).forEach((vid, i) => {
            h += swatches(c.sleeves, vid, 'sleeve', i, s.sameSleeve ? 'Sleeve color' : `Pack ${i + 1} color`);
          });
        }
        h += next('sleeves');
      }

      if (key === 'storage') {
        const n = this.tier.players;
        h += `<div class="rgs-bb__cards">`;
        h += radio('storageOn', '1', s.storageOn === true, 'Add storage', n > 1 ? `A ring or tube for each of the ${n} players${this.two ? ' (two per two-player mat)' : ''}` : 'A ring or tube for your mat', '', ' rgs-bb__card--sm');
        h += radio('storageOn', '0', s.storageOn === false, 'No storage', 'Skip this step', '', ' rgs-bb__card--sm');
        h += `</div>`;
        if (s.storageOn) {
          if (n > 1) h += tog('sameStorage', s.sameStorage, 'Same for every player');
          const ringPrice = c.ring ? (c.ring.variants.find((v) => v.ok) || c.ring.variants[0]).price : 0;
          (s.sameStorage ? [s.storage[0]] : s.storage).forEach((st, i) => {
            h += `<div class="rgs-bb__inline">`;
            if (!s.sameStorage) h += `<p class="rgs-bb__slot">Player ${i + 1}</p>`;
            if (c.ring && c.tube) {
              const pick = (v, on, title, sub) => `<label class="rgs-bb__card rgs-bb__card--sm${on ? ' is-on' : ''}"><input type="radio" name="${u}-tube-${i}" id="${u}-tube-${i}-${v}" value="${v}" data-k="tube" data-i="${i}"${on ? ' checked' : ''}><span class="rgs-bb__card-title">${bbEsc(title)}</span><span class="rgs-bb__card-text">${bbEsc(sub)}</span></label>`;
              h += `<div class="rgs-bb__cards rgs-bb__cards--pick">${pick('0', st.tube === false, c.ring.title, `${bbMoney(ringPrice, this.cur)} each, pick a color`)}${pick('1', st.tube === true, c.tube.title, `${bbMoney(c.tube.variants[0].price, this.cur)} each`)}</div>`;
            } else if (c.tube) h += `<p class="rgs-bb__fixed">${bbEsc(c.tube.title)}</p>`;
            if (c.ring && st.tube === false) h += swatches(c.ring, st.vid, 'ring', i, 'Ring color');
            h += `</div>`;
          });
        }
        h += next('storage');
      }

      if (key === 'review') {
        h += `<p class="rgs-bb__review">Everything in your bundle is listed in the summary. You can still edit any step above.</p>`;
      }
      h += `</fieldset>`;
    });
    this.steps.innerHTML = h;
    this.renderSummary();
  }

  missing(key) {
    const s = this.s;
    if (key === 'mats') return !s.type ? 'Choose custom or ready-made mats.' : `Choose ${s.mats.length > 1 && !s.sameMat ? 'every mat' : 'your mat'}.`;
    if (key === 'sleeves') return s.sleevesOn == null ? 'Add sleeves or skip this step.' : 'Choose a sleeve color.';
    if (key === 'storage') return s.storageOn == null ? 'Add storage or skip this step.' : s.storage.some((x) => x.tube === false && !x.vid) ? 'Choose a ring color.' : 'Choose a ring or tube.';
    return '';
  }

  // The cart lines chosen so far, grouped for display: [{vid, qty, label, price, role}]
  lines() {
    const c = this.cfg, s = this.s, out = [];
    if (!this.tier) return out;
    const push = (vid, label, price, role) => {
      const l = out.find((x) => x.vid === vid && x.role === role);
      if (l) l.qty++; else out.push({ vid, qty: 1, label, price, role });
    };
    s.mats.forEach((m) => {
      const p = this.matPool.find((x) => x.id === m.pid), v = p?.variants.find((x) => x.id === m.vid);
      if (v) push(v.id, v.title === 'Default Title' ? p.title : `${p.title} (${v.title})`, v.price, 'mat');
    });
    if (s.sleevesOn && c.sleeves) s.sleeves.forEach((vid) => { const v = c.sleeves.variants.find((x) => x.id === vid); if (v) push(vid, `${c.sleeves.title} (${this.vlabel(c.sleeves, v)})`, v.price, 'sleeves'); });
    if (s.storageOn) s.storage.forEach((st) => {
      if (st.tube === true && c.tube) { const v = c.tube.variants[0]; push(v.id, c.tube.title, v.price, 'storage'); }
      else if (st.tube === false && c.ring) { const v = c.ring.variants.find((x) => x.id === st.vid); if (v) push(v.id, `${c.ring.title} (${this.vlabel(c.ring, v)})`, v.price, 'storage'); }
    });
    return out;
  }

  // Discount for the choices so far: sleeves or storage for every player, or both.
  get level() {
    const s = this.s;
    const sl = s.sleevesOn === true && this.valid('sleeves'), st = s.storageOn === true && this.valid('storage');
    return sl && st ? this.cfg.pct.full : sl || st ? this.cfg.pct.part : 0;
  }

  renderSummary() {
    const cur = this.cur, pct = this.cfg.pct;
    if (!this.tier) {
      this.summary.innerHTML = `<h3 class="rgs-bb__sum-title">Your bundle</h3><p class="rgs-bb__hint">Choose a bundle to get started. Your items and savings show here as you build it.</p>`;
      return;
    }
    const lines = this.lines(), lvl = this.level;
    const sub = lines.reduce((a, l) => a + l.price * l.qty, 0);
    const off = Math.round((sub * lvl) / 100);
    const custom = this.s.type === 'custom';
    const atReview = this.keys[this.s.step] === 'review';
    let h = `<h3 class="rgs-bb__sum-title">${bbEsc(this.tier.name)}</h3>`;
    if (!lines.length) {
      h += `<p class="rgs-bb__hint">Choose your mats to see your bundle here.</p>`;
    } else {
      h += `<ul class="rgs-bb__sum-list" role="list">`;
      lines.forEach((l) => { h += `<li><span>${l.qty} × ${bbEsc(l.label)}</span><span>${bbMoney(l.price * l.qty, cur)}</span></li>`; });
      h += `</ul><dl class="rgs-bb__totals"><div><dt>Subtotal</dt><dd>${bbMoney(sub, cur)}</dd></div>`;
      if (lvl) h += `<div class="rgs-bb__off"><dt>Bundle discount (${lvl}%)</dt><dd>−${bbMoney(off, cur)}</dd></div>`;
      h += `<div class="rgs-bb__total"><dt>Total</dt><dd>${bbMoney(sub - off, cur)}</dd></div></dl>`;
      if (!lvl) h += `<p class="rgs-bb__hint">Add sleeves or storage to save ${pct.part}%, or both to save ${pct.full}%.</p>`;
      else if (lvl !== pct.full) h += `<p class="rgs-bb__hint">Add ${this.s.sleevesOn ? 'storage' : 'sleeves'} as well to save ${pct.full}%.</p>`;
    }
    if (atReview) {
      if (custom && this.cfg.text.note) h += `<p class="rgs-bb__hint">${bbEsc(this.cfg.text.note)}</p>`;
      h += `<button type="button" class="rgs-btn rgs-btn--primary rgs-bb__add" data-bb-add>${bbEsc(custom ? this.cfg.text.button_custom : this.cfg.text.button)}</button>`;
      h += `<p class="rgs-bb__error" data-bb-error hidden></p>`;
    }
    this.summary.innerHTML = h;
  }

  async add() {
    if (!this.keys.slice(0, -1).every((k) => this.valid(k))) return;
    const btn = this.querySelector('[data-bb-add]'), err = this.querySelector('[data-bb-error]');
    const custom = this.s.type === 'custom', tier = this.tier;
    const id = Math.random().toString(36).slice(2, 8).toUpperCase();
    const props = { Bundle: `${tier.name} ${id}`, _bundle: id, _bundle_mats: String(this.matCount), _bundle_players: String(tier.players) };
    const items = this.lines().filter((l) => !(custom && l.role === 'mat'))
      .map((l) => ({ id: l.vid, quantity: l.qty, properties: { ...props, _bundle_role: l.role } }));
    // Stock check before adding: Shopify adds what it can line by line, so picking a colour with too
    // few left for every player would leave half a bundle in the cart.
    const stock = {};
    const walk = (o) => {
      if (Array.isArray(o)) o.forEach(walk);
      else if (o && typeof o === 'object') {
        if ('ok' in o && 'id' in o) stock[o.id] = o;
        Object.values(o).forEach(walk);
      }
    };
    walk(this.cfg);
    const need = {};
    items.forEach((it) => { need[it.id] = (need[it.id] || 0) + it.quantity; });
    const short = Object.keys(need).find((vid) => stock[vid] && typeof stock[vid].q === 'number' && need[vid] > stock[vid].q);
    if (short) {
      const line = this.lines().find((l) => String(l.vid) === String(short));
      err.textContent = `Only ${Math.max(0, stock[short].q)} left of ${line ? line.label : 'one item'}. Please choose another option for some players.`;
      err.hidden = false;
      return;
    }
    btn.disabled = true; btn.setAttribute('aria-busy', 'true'); err.hidden = true;
    try {
      bbWrite(BB_CFG, { codes: this.cfg.codes, pct: this.cfg.pct, t: Date.now() });
      if (items.length) await bbJson('cart/add.js', { items });
      if (custom) {
        const mats = this.s.mats.map((m) => {
          const p = this.matPool.find((x) => x.id === m.pid);
          return { vid: m.vid, url: `${p.url}?variant=${m.vid}#design`, done: false };
        });
        // Drop abandoned pending bundles (no lines in the cart) so they can't claim this bundle's mats.
        const pend = [...BundleGuard.pending(), { name: tier.name, props, mats, extras: items.length > 0, t: Date.now() }];
        const cart = await bbJson('cart.js').catch(() => null);
        bbWrite(BB_PENDING, cart ? bbPrune(pend, bbCartIds(cart)) : pend);
        location.href = mats[0].url;
      } else {
        await BundleGuard.run();
        location.href = root + 'cart';
      }
    } catch (e) {
      err.textContent = e.message || 'Something went wrong. Please try again.';
      err.hidden = false;
      btn.disabled = false; btn.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('rgs-bundle-builder')) customElements.define('rgs-bundle-builder', RgsBundleBuilder);

/* ---------------- Add-on offer after Add to Cart (blocks/rgs-addon-offer) ----------------
   After the main product form on this page adds an item, a native modal <dialog> says "Added to
   your cart" and offers Gaming Essentials from the block's collection: one-click Add for
   single-variant products, color chips (the bundle builder's .rgs-bb__ chips and BB_SWATCH palette)
   plus Add for the rest. Detection: Horizon's <product-form-component> dispatches the standard
   CartLinesUpdateEvent (action "add") with a promise that resolves once /cart/add.js and the cart
   refresh are done; the offer only waits on that promise, so the add itself is never delayed.
   Events from anything else (quick add dialogs, <rgs-companion>, the bundle builder, this offer's
   own Add buttons) are ignored because their target is not this section's product form.
   Not shown in artwork mode, when the added product is in the offer collection, when nothing is
   left to offer (optionally skipping products already in the cart), or a second time in a visit
   when "Show once per visit" is on (sessionStorage). Adds post to /cart/add.js and dispatch the
   same CartLinesUpdateEvent as the companion, so the cart icon and drawer update. With Horizon's
   "auto open cart drawer" setting on, the drawer opens as usual and the offer stacks on top of it;
   adds made inside the offer make Horizon wait for the offer to close before opening the drawer.
   Product text is set with textContent only. */
try {
  const AO_SEEN = 'rgsAddonOfferShown';
  const aoImg = (u, w) => {
    if (!u) return '';
    try { const x = new URL(u, location.href); x.searchParams.set('width', String(w)); return x.href; } catch { return ''; }
  };
  // Whole-word match against the bundle builder palette, longest name first ("dark blue" before "blue").
  const aoKeys = Object.keys(BB_SWATCH).sort((a, b) => b.length - a.length);
  const aoSwatch = (name) => {
    const s = ` ${String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
    for (const k of aoKeys) if (s.includes(` ${k} `)) return BB_SWATCH[k];
    if (/ (clear|transparent) /.test(s)) return '#dde7ea';
    return '#c6c6c6';
  };
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  let aoUid = 0;

  class RgsAddonOffer extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      try { this.cfg = JSON.parse(this.querySelector('[data-rgs-addon-data]')?.textContent || '{}') || {}; } catch { this.cfg = {}; }
      this.labels = this.cfg.labels || {};
      this.dialog = this.querySelector('[data-rgs-addon-dialog]');
      this.onLines = (e) => { try { this.onCartLines(e); } catch { /* never break the cart */ } };
      document.addEventListener(StandardEvents.cartLinesUpdate, this.onLines);
      const d = this.dialog;
      if (!d || d.dataset.rgsWired) return;
      d.dataset.rgsWired = '1';
      d.querySelector('[data-rgs-addon-close]')?.addEventListener('click', () => d.close());
      d.querySelector('[data-rgs-addon-continue]')?.addEventListener('click', () => d.close());
      d.querySelector('[data-rgs-addon-cart]')?.addEventListener('click', () => this.viewCart());
      // A click on the backdrop lands on the <dialog> itself (the panel fills its box).
      d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
      d.addEventListener('close', () => this.onClose());
    }

    disconnectedCallback() {
      this.started = false;
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.onLines);
      document.documentElement.classList.remove('rgs-addon-open');
    }

    seen() { try { return sessionStorage.getItem(AO_SEEN) === '1'; } catch { return false; } }

    artwork() { return document.documentElement.classList.contains('rgs-artwork-mode'); }

    // Only adds from the main product form in this block's section count.
    onCartLines(e) {
      if (!e || e.action !== 'add' || !e.promise) return;
      const src = e.target;
      if (!(src instanceof Element) || src.localName !== 'product-form-component') return;
      if (src.closest('dialog')) return;
      const sec = this.closest('.shopify-section');
      if (!sec || src.closest('.shopify-section') !== sec) return;
      if (this.dialog?.open || this.pending || this.artwork()) return;
      if (this.cfg.once && this.seen()) return;
      const active = document.activeElement;
      const opener = active instanceof HTMLElement && active !== document.body ? active : null;
      // merchandiseId may be a plain id or a gid://shopify/ProductVariant/<id>.
      const variantId = (String((e.lines && e.lines[0] && e.lines[0].merchandiseId) || '').match(/(\d+)\s*$/) || [])[1] || '';
      this.pending = true;
      Promise.resolve(e.promise)
        .then((res) => {
          const detail = (res && res.detail) || {};
          if (!res || detail.didError) return null;
          return this.prepare(variantId, opener, detail.items);
        })
        .catch(() => null)
        .finally(() => { this.pending = false; });
    }

    async prepare(variantId, opener, fallbackItems) {
      let items = null;
      try {
        const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (res.ok) items = (await res.json()).items || null;
      } catch { /* use what the event gave us */ }
      if (!items) items = Array.isArray(fallbackItems) ? fallbackItems : [];
      const line = items.find((i) => String(i.variant_id) === variantId) || null;
      const addedId = String((line && line.product_id) || (this.cfg.product && this.cfg.product.id) || '');
      const essentials = new Set((this.cfg.ids || []).map(String));
      if (this.cfg.self || essentials.has(addedId)) return;
      if (this.artwork() || (this.cfg.once && this.seen()) || !this.dialog || this.dialog.open) return;
      const inCart = new Set(items.map((i) => String(i.product_id)));
      const max = Math.max(1, Number(this.cfg.max) || 3);
      const list = (this.cfg.products || [])
        .filter((p) => p && String(p.id) !== addedId && (p.variants || []).some((v) => v.available))
        .filter((p) => !this.cfg.skipInCart || !inCart.has(String(p.id)))
        .slice(0, max);
      if (!list.length) return;
      if (!this.isConnected || this.otherModal()) return;
      this.renderAdded(line);
      this.renderList(list);
      const cartBtn = this.dialog.querySelector('[data-rgs-addon-cart]');
      if (cartBtn) cartBtn.textContent = this.labels.viewCart || 'No thanks, view cart';
      this.opener = opener;
      this.toCart = false;
      try { this.dialog.showModal(); } catch { return; }
      document.documentElement.classList.add('rgs-addon-open');
      if (this.cfg.once) { try { sessionStorage.setItem(AO_SEEN, '1'); } catch { /* storage off */ } }
    }

    // Another modal (a quick add, the zoom gallery) is open: don't stack on it. The cart drawer is fine.
    otherModal() {
      return [...document.querySelectorAll('dialog[open]')].some((d) => {
        if (d === this.dialog || d.closest('theme-drawer')) return false;
        try { return d.matches(':modal'); } catch { return false; }
      });
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'USD' }).format((Number(cents) || 0) / 100);
      } catch { return '$' + ((Number(cents) || 0) / 100).toFixed(2); }
    }

    renderAdded(line) {
      const box = this.dialog.querySelector('[data-rgs-addon-added]');
      if (!box) return;
      const p = this.cfg.product || {};
      const src = aoImg((line && line.image) || p.image, 120);
      const media = el('div', 'rgs-addon__added-media');
      if (src) {
        const img = el('img');
        img.src = src; img.alt = ''; img.width = 60; img.height = 60;
        media.append(img);
      }
      const info = el('div', 'rgs-addon__added-info');
      info.append(el('p', 'rgs-addon__added-title', (line && line.product_title) || p.title || ''));
      const vt = line && line.variant_title;
      if (vt && !/^default title$/i.test(vt)) info.append(el('p', 'rgs-addon__added-variant', vt));
      if (line && line.final_price != null) info.append(el('p', 'rgs-addon__added-price', this.money(line.final_price)));
      box.replaceChildren(media, info);
    }

    renderList(list) {
      const ul = this.dialog.querySelector('[data-rgs-addon-list]');
      if (!ul) return;
      ul.replaceChildren(...list.map((p) => this.renderItem(p)));
    }

    renderItem(p) {
      const L = this.labels;
      const li = el('li', 'rgs-addon__item');
      const row = el('div', 'rgs-addon__row');
      const href = /^\/(?!\/)/.test(p.url || '') ? p.url : '#';
      const media = el('a', 'rgs-addon__media');
      media.href = href; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true');
      if (p.image) {
        const img = el('img');
        img.src = aoImg(p.image, 160); img.alt = ''; img.width = 64; img.height = 64; img.loading = 'lazy';
        media.append(img);
      }
      const info = el('div', 'rgs-addon__info');
      const title = el('a', 'rgs-addon__title', p.title || '');
      title.href = href;
      const price = el('p', 'rgs-addon__price');
      const variants = (p.variants || []).filter((v) => !/default title/i.test(v.title || '') || (p.variants || []).length === 1);
      const multi = variants.length > 1;
      const avail = variants.filter((v) => v.available);
      const prices = avail.map((v) => Number(v.price) || 0);
      const lo = prices.length ? Math.min(...prices) : Number(p.price) || 0;
      price.textContent = this.money(lo);
      info.append(title, price);
      const btn = el('button', 'rgs-btn rgs-btn--primary rgs-addon__add', L.add || 'Add');
      btn.type = 'button';
      btn.setAttribute('aria-label', `${L.add || 'Add'} ${p.title || ''}`.trim());
      row.append(media, info, btn);
      li.append(row);

      let picked = multi ? null : (avail[0] || null);
      if (multi) {
        const u = `rgs-ao-${++aoUid}`;
        const opt = String(p.option || 'option').toLowerCase();
        const fs = el('fieldset', 'rgs-addon__swatches');
        const legend = el('legend', 'rgs-addon__legend');
        legend.append(`${L.choose || 'Choose a'} ${opt}`);
        const chosen = el('b');
        legend.append(chosen);
        const grid = el('div', 'rgs-bb__swatch-grid rgs-addon__grid');
        variants.forEach((v) => {
          const label = el('label', `rgs-bb__swatch${v.available ? '' : ' is-out'}`);
          label.title = v.available ? v.title : `${v.title} (${L.out || 'Out of stock'})`;
          const input = el('input');
          input.type = 'radio'; input.name = u; input.value = String(v.id); input.disabled = !v.available;
          const chip = el('span', 'rgs-bb__chip');
          chip.style.setProperty('--c', aoSwatch(v.title));
          chip.setAttribute('aria-hidden', 'true');
          const name = el('span', 'rgs-bb__swatch-name', v.title || '');
          if (!v.available) name.append(el('small', '', L.out || 'Out of stock'));
          label.append(input, chip, name);
          input.addEventListener('change', () => {
            if (!input.checked) return;
            picked = v;
            grid.querySelectorAll('.rgs-bb__swatch').forEach((x) => x.classList.toggle('is-on', x === label));
            chosen.textContent = `: ${v.title}`;
            price.textContent = this.money(v.price);
            msg.textContent = ''; msg.classList.remove('is-error');
            if (btn.classList.contains('is-added')) { btn.classList.remove('is-added'); btn.textContent = L.add || 'Add'; btn.disabled = false; }
          });
          grid.append(label);
        });
        fs.append(legend, grid);
        li.append(fs);
      }
      const msg = el('p', 'rgs-addon__msg');
      msg.setAttribute('role', 'status');
      li.append(msg);

      btn.addEventListener('click', () => {
        if (!picked) {
          msg.textContent = L.chooseFirst || 'Pick an option first.';
          msg.classList.add('is-error');
          li.querySelector('input[type="radio"]:not(:disabled)')?.focus();
          return;
        }
        this.add(picked, btn, msg);
      });
      return li;
    }

    async add(v, btn, msg) {
      if (btn.disabled) return;
      const L = this.labels;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      msg.textContent = ''; msg.classList.remove('is-error');
      const items = [{ id: Number(v.id), quantity: 1 }];
      const deferred = CartLinesUpdateEvent.createPromise();
      btn.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'dialog',
        lines: [{ merchandiseId: String(v.id), quantity: 1 }],
        promise: deferred.promise,
      }));
      let ok = false;
      try {
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        ok = res.ok;
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: !res.ok, items: cart.items, itemCount: 1, source: 'rgs-addon-offer' },
        });
        if (!ok) { msg.textContent = data.description || data.message || L.error || 'Could not add to cart.'; msg.classList.add('is-error'); }
      } catch (err) {
        deferred.reject(err);
        msg.textContent = L.error || 'Could not add to cart.';
        msg.classList.add('is-error');
      }
      btn.removeAttribute('aria-busy');
      if (ok) {
        btn.classList.add('is-added');
        btn.textContent = L.added || 'Added';
        msg.textContent = `${L.added || 'Added'}: ${v.title && !/^default title$/i.test(v.title) ? v.title : ''}`.replace(/: $/, '');
        const cartBtn = this.dialog.querySelector('[data-rgs-addon-cart]');
        if (cartBtn) cartBtn.textContent = L.viewCartAfter || 'View cart';
      } else {
        btn.disabled = false;
      }
    }

    drawer() {
      const td = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      return td && typeof td.open === 'function' ? td : null;
    }

    viewCart() {
      const td = this.drawer();
      this.toCart = true;
      this.dialog.close();
      if (td) { if (!td.isOpen) requestAnimationFrame(() => td.open()); }
      else location.href = this.cfg.cartUrl || root + 'cart';
    }

    onClose() {
      document.documentElement.classList.remove('rgs-addon-open');
      const td = this.drawer();
      if (this.toCart || (td && td.isOpen)) return;
      const target = this.opener && this.opener.isConnected ? this.opener
        : this.closest('.shopify-section')?.querySelector('product-form-component [ref="addToCartButton"]');
      try { target?.focus({ preventScroll: true }); } catch { /* focus is best effort */ }
    }
  }
  if (!customElements.get('rgs-addon-offer')) customElements.define('rgs-addon-offer', RgsAddonOffer);
} catch (err) { /* never break the rest of the module */ }

// ── Jump to the designer ─────────────────────────────────────
// Links ending in #design (the Playmat Studio size chooser, the bundle builder) land on the
// product page scrolled to the Playmat Studio editor. The embed shows the editor only after it
// has checked the product, so wait for it to become visible, then scroll it into view below the
// sticky header and flash an outline. Its start buttons live in a cross-origin iframe, so the
// customer still makes the first click (a file picker needs a real click anyway).
(() => {
  if (location.hash !== '#design') return;
  const started = Date.now();
  const tick = () => {
    const el = document.getElementById('playmat-designer-root');
    const frame = el && el.querySelector('iframe');
    const target = frame && frame.getBoundingClientRect().height > 40 ? frame : null;
    if (!target) {
      if (Date.now() - started < 10000) setTimeout(tick, 250);
      return;
    }
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sc = rgsScroller();
    const offset = target.getBoundingClientRect().top - header - 24;
    sc.scrollTo({ top: (sc === window ? window.scrollY : sc.scrollTop) + offset, behavior: reduce ? 'auto' : 'smooth' });
    el.classList.add('rgs-design-flash');
    setTimeout(() => el.classList.remove('rgs-design-flash'), 2400);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tick);
  else tick();
})();

// ── Special request box behind a link ────────────────────────
// Horizon's product custom property block (a heading and a text box) stays closed behind a small
// "+ Add a special request" button until clicked, so Add to Cart sits higher in the column. The
// label is Theme settings > Rubicon style > "Special request link" (data-rgs-note-label on <html>,
// from snippets/rgs-theme-options.liquid); empty leaves the box open. The text box itself is never
// moved or renamed (it joins the product form through its form attribute), and a box that already
// holds text, is required, or is selected in the theme editor stays open. Horizon can morph the
// block on a variant change, so the button is re-added whenever it goes missing.
try {
  (() => {
    const html = document.documentElement;
    const SEL = 'product-custom-property-component';
    let uid = 0;
    const setOpen = (comp, btn, open) => {
      btn.setAttribute('aria-expanded', String(open));
      comp.dataset.rgsNote = open ? 'open' : 'closed';
    };
    const enhance = (comp) => {
      const label = html.dataset.rgsNoteLabel;
      const panel = comp.querySelector(':scope > .spacing-style');
      const input = panel && panel.querySelector('.custom-property__input');
      if (!label || !panel || !input || input.required) return;
      let btn = comp.querySelector(':scope > .rgs-note-toggle');
      if (!panel.id) panel.id = `rgs-note-${++uid}`;
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rgs-note-toggle';
        const icon = document.createElement('span');
        icon.className = 'rgs-note-toggle__icon';
        icon.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span');
        text.textContent = label;
        btn.append(icon, text);
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') !== 'true';
          setOpen(comp, btn, open);
          if (open) { const f = comp.querySelector('.custom-property__input'); if (f) f.focus(); }
        });
        comp.insertBefore(btn, panel);
        setOpen(comp, btn, !!input.value.trim());
      } else {
        // Re-apply after a morph that reset the block's attributes.
        setOpen(comp, btn, btn.getAttribute('aria-expanded') === 'true');
      }
      btn.setAttribute('aria-controls', panel.id);
    };
    // The label is only set on product pages (snippets/rgs-theme-options); without it there is nothing to do.
    if (!html.dataset.rgsNoteLabel) return;
    const run = () => { try { document.querySelectorAll(SEL).forEach(enhance); } catch (e) { /* never break the page */ } };
    let timer;
    const later = () => { clearTimeout(timer); timer = setTimeout(run, 60); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
    new MutationObserver(later).observe(document.body, { childList: true, subtree: true });
    // Theme editor: open the box while its block is selected.
    document.addEventListener('shopify:block:select', (e) => {
      try {
        const comp = e.target instanceof Element && e.target.closest(SEL);
        const btn = comp && comp.querySelector(':scope > .rgs-note-toggle');
        if (btn) setOpen(comp, btn, true);
      } catch (err) { /* ignore */ }
    });
  })();
} catch (err) { /* never break the rest of the module */ }

// ── Artwork mode: no native add to cart without artwork ──────
// A mat printed with the customer's own artwork must be bought through Playmat Studio, which adds it
// to the cart itself once the artwork is uploaded. On a product page with Studio
// (#playmat-designer-root), artwork mode is on when the selected variant has an option value
// containing "custom artwork" (templates/product.json products with a Custom Artwork color), or on
// the product.custom-mats template (data-rgs-template, from snippets/rgs-theme-options.liquid).
// It puts rgs-artwork-mode on <html>, and rubicon-product.css hides that section's buy buttons and
// Horizon's sticky add-to-cart bar. Horizon re-renders the section when the variant changes, so
// everything is read from the document again each time.
(() => {
  if (!document.getElementById('playmat-designer-root')) return;
  const html = document.documentElement;
  const check = () => {
    const studio = document.getElementById('playmat-designer-root');
    const section = studio && (studio.closest('.shopify-section') || document);
    let on = false;
    if (section) {
      if (html.dataset.rgsTemplate === 'product.custom-mats') on = true;
      else {
        const values = [
          ...[...section.querySelectorAll('variant-picker input[type="radio"]:checked')].map((i) => i.value),
          ...[...section.querySelectorAll('variant-picker select')].map((s) => s.value),
        ];
        on = values.some((v) => /custom artwork/i.test(v || ''));
      }
    }
    html.classList.toggle('rgs-artwork-mode', on);
  };
  let timer;
  const later = () => { clearTimeout(timer); timer = setTimeout(check, 60); };
  document.addEventListener('change', () => { check(); later(); }, true);
  if (StandardEvents && StandardEvents.productSelect) document.addEventListener(StandardEvents.productSelect, later, true);
  new MutationObserver(later).observe(document.body, { childList: true, subtree: true });
  check();
})();

// ── BixGrow sign-up and log-in in a pop-up window ────────────
// BixGrow's register and login pages can't run inside the page (an iframe or lightbox loses
// their session cookie), so on desktop they open in a centred pop-up window instead of taking
// the visitor away from the store. Phones, and browsers that block pop-ups, follow the link.
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('a[href*="bixgrow.com/"]');
  if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  let url;
  try { url = new URL(a.href); } catch (err) { return; }
  if (!/\/(register|login)\/?$/.test(url.pathname) || matchMedia('(max-width: 749px)').matches) return;
  const w = Math.min(560, screen.availWidth - 40);
  const h = Math.min(820, screen.availHeight - 80);
  const left = Math.round((screen.availWidth - w) / 2 + (screen.availLeft || 0));
  const top = Math.round((screen.availHeight - h) / 2 + (screen.availTop || 0));
  const win = window.open(url.href, 'rgs-bixgrow', `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
  if (win) { e.preventDefault(); win.focus(); }
});

/* ---------------- Artifex Guild artist filter ----------------
 * sections/rgs-artist-directory: style chips show only the artist cards whose data-styles
 * contain the chosen style. Reads ?style=<style> on load (profile pages link to it).
 * Wrapped in try/catch so a problem here can't stop the cart or other components. */
try {
  class RgsArtistFilter extends HTMLElement {
    connectedCallback() {
      try {
        this.bar = this.querySelector('[data-rgs-afilter]');
        if (!this.bar) return;
        this.chips = [...this.bar.querySelectorAll('button[data-filter]')];
        this.cards = [...this.querySelectorAll('.rgs-acard')];
        this.none = this.querySelector('[data-rgs-afilter-none]');
        this.status = this.querySelector('[data-rgs-afilter-status]');
        this.bar.hidden = false;
        this.bar.addEventListener('click', (e) => {
          const chip = e.target.closest && e.target.closest('button[data-filter]');
          if (chip) this.apply(chip.dataset.filter, true);
        });
        let start = '';
        try { start = new URL(window.location.href).searchParams.get('style') || ''; } catch (err) { start = ''; }
        if (start && this.chips.some((c) => c.dataset.filter === start)) this.apply(start, false);
      } catch (err) {
        console.warn('rgs-artist-filter', err);
      }
    }
    apply(key, announce) {
      let shown = 0;
      this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.filter === key)));
      this.cards.forEach((card) => {
        const match = !key || (card.dataset.styles || '').includes(`|${key}|`);
        card.hidden = !match;
        if (match) shown += 1;
      });
      if (this.none) this.none.hidden = shown > 0;
      if (announce && this.status) this.status.textContent = `${shown} ${this.status.dataset.label || ''}`.trim();
    }
  }
  if (!customElements.get('rgs-artist-filter')) customElements.define('rgs-artist-filter', RgsArtistFilter);
} catch (err) {
  console.warn('rgs-artist-filter', err);
}

/* ---------------- Deferred sections and #anchor links ----------------
   Homepage sections from the 4th on use content-visibility: auto (data-rgs-defer, see
   snippets/rgs-section-style). Until a section has been shown the browser only guesses its height,
   so arriving on a link like /#reviews could stop in the wrong place. Show every deferred section
   above the target for real, then scroll to it again. Runs on load with a #hash and on hash changes. */
try {
  const rgsDeferredJump = (smooth) => {
    let id = '';
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (err) { id = ''; }
    const target = id && document.getElementById(id);
    if (!target) return;
    const before = [...document.querySelectorAll('[data-rgs-defer]')].filter((s) => s === target || s.contains(target)
      || (s.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING));
    if (!before.length) return;
    before.forEach((s) => { s.style.contentVisibility = 'visible'; });
    target.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'instant' });
  };
  if (document.querySelector('[data-rgs-defer]')) {
    let userScrolled = false;
    const mark = () => { userScrolled = true; };
    ['wheel', 'touchstart', 'keydown'].forEach((t) => window.addEventListener(t, mark, { once: true, passive: true }));
    if (window.location.hash) {
      rgsDeferredJump(false);
      // Images and app widgets above the target can still change size while the page loads.
      window.addEventListener('load', () => { if (!userScrolled) rgsDeferredJump(false); }, { once: true });
    }
    window.addEventListener('hashchange', () => rgsDeferredJump(!reduceMotion.matches));
  }
} catch (err) {
  console.warn('rgs-deferred-jump', err);
}

/* ---------------- Timeline map layout (sections/rgs-timeline, layout "Map") ---------------- */
// "All roads lead to the hub": draws a stylised SVG map into the empty .rgs-tl__map from the milestone list
// (the list stays the readable content; the map is aria-hidden). The hub sits in the centre, each road (1-4)
// curves outwards through its milestones ("cities") in list order, and an optional river crosses road 1.
// Each road is drawn twice: dashed (unexplored) and a solid accent copy whose dash offset reveals it up to the
// last city reached. As each card crosses the reading line (the middle of the screen; on phones the middle of
// the space under the sticky map) its road draws to its city, the city lights and the card is marked active;
// scrolling back un-lights them. Reduced motion and the theme editor get the finished map with no tracking.
// Deterministic: no randomness, so the map is the same on every load. Rebuilt when the phone/desktop size changes.
const SVG_NS = 'http://www.w3.org/2000/svg';
const rgsSvg = (tag, attrs, parent) => {
  const n = document.createElementNS(SVG_NS, tag);
  if (attrs) Object.keys(attrs).forEach((k) => { if (attrs[k] != null) n.setAttribute(k, attrs[k]); });
  if (parent) parent.appendChild(n);
  return n;
};
let rgsTlMapUid = 0;
const rgsTimelineMap = (tl) => {
  const box = tl.querySelector('.rgs-tl__map');
  const items = [...tl.querySelectorAll('.rgs-tl__item')];
  if (!box || !items.length) return;
  const d = box.dataset;
  const uid = `rgs-tl-map-${++rgsTlMapUid}`;
  const still = reduceMotion.matches || !!window.Shopify?.designMode;
  const phone = window.matchMedia('(max-width: 989px)');
  const C = 500;
  const R = 470; // radius scale for the cities (the farthest sits at 90%)
  const EDGE = 34; // roads stop this far inside the frame
  const fmt = (n) => Math.round(n * 10) / 10;
  const stops = items.map((it, i) => ({
    it,
    i,
    road: it.dataset.road || '1',
    name: (it.dataset.city || '').trim(),
    date: (it.querySelector('.rgs-tl__date')?.textContent || '').trim(),
    major: it.classList.contains('rgs-tl__item--major'),
    icon: it.querySelector('.rgs-tl__node .rgs-icon'),
  }));
  const roadIds = ['1', '2', '3', '4'].filter((r) => stops.some((s) => s.road === r));
  const hubStop = stops.find((s) => s.road === 'hub');
  let view = null;
  let cur = -2;

  const build = () => {
    box.textContent = '';
    cur = -2;
    const panel = document.createElement('div');
    panel.className = 'rgs-tl-map';
    box.appendChild(panel);
    const width = panel.getBoundingClientRect().width || 600;
    // Label size in map units, so names stay about 11.5px on screen or larger.
    const fs = Math.min(34, Math.max(22, (11.5 * 1000) / width));
    // Small maps (phones): while scrolling only the active city is named; the finished map names every
    // city without dates.
    const compact = width < 560;
    const solo = compact && !still;
    const svg = rgsSvg('svg', { viewBox: '0 0 1000 1000', class: 'rgs-tl-map__svg', focusable: 'false', 'aria-hidden': 'true' }, panel);
    if (solo) svg.classList.add('is-solo');
    else if (compact) svg.classList.add('is-compact');
    const defs = rgsSvg('defs', null, svg);
    const glow = rgsSvg('radialGradient', { id: `${uid}-glow` }, defs);
    rgsSvg('stop', { offset: '0', 'stop-color': 'var(--rgs-orange)', 'stop-opacity': '.55' }, glow);
    rgsSvg('stop', { offset: '1', 'stop-color': 'var(--rgs-orange)', 'stop-opacity': '0' }, glow);

    // Terrain: grid, range rings, compass rose, stitched frame.
    const terrain = rgsSvg('g', { class: 'rgs-tl-map__terrain' }, svg);
    let grid = '';
    for (let g = 100; g < 1000; g += 100) grid += `M${g} 0V1000M0 ${g}H1000`;
    rgsSvg('path', { d: grid, class: 'rgs-tl-map__grid' }, terrain);
    [150, 290, 430, 570].forEach((r) => rgsSvg('circle', { cx: C, cy: C, r, class: 'rgs-tl-map__ring' }, terrain));
    const rose = rgsSvg('g', { class: 'rgs-tl-map__rose' }, terrain);
    rgsSvg('circle', { r: 30 }, rose);
    rgsSvg('path', { d: 'M0-44L7-7 44 0 7 7 0 44-7 7-44 0-7-7z', class: 'rgs-tl-map__rose-star' }, rose);
    rgsSvg('path', { d: 'M0-44L7-7H-7z', class: 'rgs-tl-map__rose-n' }, rose);
    const north = rgsSvg('text', { x: 0, y: -52, 'text-anchor': 'middle', class: 'rgs-tl-map__rose-label' }, rose);
    north.textContent = 'N';
    rgsSvg('rect', { x: 14, y: 14, width: 972, height: 972, rx: 18, class: 'rgs-tl-map__frame' }, terrain);

    const riverLayer = rgsSvg('g', { class: 'rgs-tl-map__river' }, svg);
    const roadLayer = rgsSvg('g', { class: 'rgs-tl-map__roads' }, svg);
    const cityLayer = rgsSvg('g', { class: 'rgs-tl-map__cities' }, svg);
    const labelLayer = rgsSvg('g', { class: 'rgs-tl-map__labels' }, svg);

    // Roads: start on the diagonals and curve anticlockwise, with a gentle wiggle seeded by the road number.
    const n = roadIds.length;
    const inside = ([x, y]) => x > EDGE && x < 1000 - EDGE && y > EDGE && y < 1000 - EDGE;
    const roads = roadIds.map((id, k) => {
      const seed = Number(id);
      const mean = ((-45 + (k * 360) / Math.max(n, 1)) * Math.PI) / 180;
      const bend = -0.38;
      const a0 = mean + 0.05;
      const theta = (r) => a0 + bend * (r / R) + 0.08 * Math.sin(r / 62 + seed * 1.9) * Math.min(1, r / 120);
      const at = (r) => [C + r * Math.cos(theta(r)), C + r * Math.sin(theta(r))];
      const own = stops.filter((s) => s.road === id);
      own.forEach((s, j) => { s.r = own.length === 1 ? R * 0.6 : R * (0.3 + (0.6 * j) / (own.length - 1)); });
      const rs = [];
      for (let r = 0; r < 900 && inside(at(r)); r += 6) rs.push(r);
      own.forEach((s) => rs.push(s.r));
      rs.sort((a, b) => a - b);
      const pts = rs.map(at);
      const lens = [0];
      for (let j = 1; j < pts.length; j++) lens.push(lens[j - 1] + Math.hypot(pts[j][0] - pts[j - 1][0], pts[j][1] - pts[j - 1][1]));
      own.forEach((s) => {
        const j = rs.indexOf(s.r);
        s.p = pts[j];
        s.len = lens[j];
        const a = at(s.r - 4);
        const b = at(s.r + 4);
        const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
        s.t = [(b[0] - a[0]) / l, (b[1] - a[1]) / l];
      });
      const dAttr = pts.map((p, j) => `${j ? 'L' : 'M'}${fmt(p[0])} ${fmt(p[1])}`).join('');
      const total = lens[lens.length - 1];
      rgsSvg('path', { d: dAttr, class: 'rgs-tl-map__road' }, roadLayer);
      const lit = [
        rgsSvg('path', { d: dAttr, class: 'rgs-tl-map__lit rgs-tl-map__lit--glow' }, roadLayer),
        rgsSvg('path', { d: dAttr, class: 'rgs-tl-map__lit' }, roadLayer),
      ];
      lit.forEach((p) => { p.style.strokeDasharray = `${fmt(total + 2)} ${fmt(total + 2)}`; p.style.strokeDashoffset = fmt(total + 1); });
      // Road name: placed later, as a map region label near where the road leaves the map.
      const end = pts[pts.length - 1];
      const before = pts[Math.max(0, pts.length - 4)];
      const el = Math.hypot(end[0] - before[0], end[1] - before[1]) || 1;
      const name = (d[`road-${id}`] || '').trim();
      const endT = [(end[0] - before[0]) / el, (end[1] - before[1]) / el];
      return { id, own, total, lit, at, end, endT, name, pts };
    });

    // River: crosses road 1 between its first two cities, then bends away from the hub on both sides (a
    // frontier the empire crossed), so it stays clear of the other roads. Its name sits on the emptier side.
    if (d.river === 'true' && roads.length) {
      const r1 = roads[0];
      const rx = r1.own.length > 1 ? (r1.own[0].r + r1.own[1].r) / 2 : R * 0.42;
      const X = r1.at(rx);
      const X2 = r1.at(rx + 4);
      const tl0 = Math.hypot(X2[0] - X[0], X2[1] - X[1]) || 1;
      const v = [-(X2[1] - X[1]) / tl0, (X2[0] - X[0]) / tl0];
      const ul = Math.hypot(X[0] - C, X[1] - C) || 1;
      const u = [(X[0] - C) / ul, (X[1] - C) / ul];
      const riverAt = (s) => {
        const w = 18 * Math.sin(s / 70) + 8 * Math.sin(s / 31 + 1.3);
        const off = 0.0011 * s * s + w;
        return [X[0] + v[0] * s + u[0] * off, X[1] + v[1] * s + u[1] * off];
      };
      const keep = ([x, y]) => x > -60 && x < 1060 && y > -60 && y < 1060;
      const pts = [];
      for (let s = -900; s <= 900; s += 8) { const q = riverAt(s); if (keep(q)) pts.push(q); }
      const path = (list) => list.map((q, j) => `${j ? 'L' : 'M'}${fmt(q[0])} ${fmt(q[1])}`).join('');
      const rd = path(pts);
      rgsSvg('path', { d: rd, class: 'rgs-tl-map__river-bank' }, riverLayer);
      rgsSvg('path', { d: rd, class: 'rgs-tl-map__river-water' }, riverLayer);
      const rl = (d.riverLabel || '').trim();
      if (rl) {
        const seg = (sign) => { const out = []; for (let s = 120; s <= 420; s += 8) out.push(riverAt(s * sign)); return out; };
        const room = (list) => {
          const m = list[Math.floor(list.length / 2)];
          if (!keep(m) || m[0] < 60 || m[0] > 940 || m[1] < 60 || m[1] > 940) return -1;
          return Math.min(...stops.filter((q) => q.p).map((q) => Math.hypot(q.p[0] - m[0], q.p[1] - m[1])));
        };
        const a = seg(1);
        const b = seg(-1);
        const best = room(a) >= room(b) ? a : b;
        if (best[best.length - 1][0] < best[0][0]) best.reverse();
        const rid = `${uid}-river`;
        rgsSvg('path', { id: rid, d: path(best), fill: 'none', stroke: 'none' }, defs);
        const txt = rgsSvg('text', { class: 'rgs-tl-map__river-name', dy: fmt(-fs * 0.85), 'font-size': fmt(fs * 0.95) }, riverLayer);
        const tp = rgsSvg('textPath', { href: `#${rid}`, startOffset: '50%', 'text-anchor': 'middle' }, txt);
        tp.textContent = rl;
      }
    }

    // Hub: the golden milestone.
    const hub = rgsSvg('g', { class: 'rgs-tl-map__hub', transform: `translate(${C} ${C})` }, cityLayer);
    rgsSvg('circle', { r: 120, class: 'rgs-tl-map__hub-glow', fill: `url(#${uid}-glow)` }, hub);
    rgsSvg('circle', { r: 46, class: 'rgs-tl-map__hub-ping' }, hub);
    const hubImg = (d.hubImage || '').trim();
    if (hubImg) {
      // A picked image (the die logo) replaces the disc and icon; it is about the disc's size.
      rgsSvg('image', { href: hubImg, x: -54, y: -48, width: 108, height: 96, preserveAspectRatio: 'xMidYMid meet', class: 'rgs-tl-map__hub-img' }, hub);
    } else {
      rgsSvg('circle', { r: 46, class: 'rgs-tl-map__hub-disc' }, hub);
      rgsSvg('circle', { r: 37, class: 'rgs-tl-map__hub-ring' }, hub);
    }
    const hubIcon = hubImg ? null : hubStop?.icon;
    if (hubImg) {
      // The image is the hub's mark; no icon or dot on top.
    } else if (hubIcon) {
      const ic = hubIcon.cloneNode(true);
      ['x', 'y'].forEach((k) => ic.setAttribute(k, -22));
      ['width', 'height'].forEach((k) => ic.setAttribute(k, 44));
      ic.setAttribute('class', 'rgs-tl-map__icon');
      hub.appendChild(ic);
    } else {
      rgsSvg('circle', { r: 8, class: 'rgs-tl-map__hub-dot' }, hub);
    }
    // Everything a label must avoid: hub, compass, city nodes, then each label as it is placed.
    const placed = [[C - 50, C - 50, C + 50, C + 50]];
    // Compass rose in the corner farthest from every road.
    const corners = [[80, 80], [920, 80], [920, 920], [80, 920]];
    let roseAt = corners[3];
    let roseRoom = -1;
    corners.forEach((c) => {
      let m = Infinity;
      roads.forEach((road) => road.pts.forEach((q) => { m = Math.min(m, Math.hypot(q[0] - c[0], q[1] - c[1])); }));
      if (m > roseRoom + 40) { roseRoom = m; roseAt = c; }
    });
    rose.setAttribute('transform', `translate(${roseAt[0]} ${roseAt[1] + 4})`);
    placed.push([roseAt[0] - 46, roseAt[1] - 70, roseAt[0] + 46, roseAt[1] + 50]);

    // Cities and their labels.
    const cities = [];
    const nodes = stops.filter((s) => s.p).map((s) => ({ s, rad: s.major ? 25 : 18 }));
    nodes.forEach(({ s, rad }) => placed.push([s.p[0] - rad, s.p[1] - rad, s.p[0] + rad, s.p[1] + rad]));
    const overlap = (a, b) => Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) * Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
    const clip = (str, max) => (str.length > max ? `${str.slice(0, max - 1).trimEnd()}…` : str);
    // Tries each candidate spot ([direction, distance] from x, y) for a lw x lh label; returns the one that
    // overlaps the least of what is already placed and stays inside the frame.
    const pick = (x, y, cands, lw, lh) => {
      let best = null;
      cands.forEach(([nv, gap]) => {
        const ax = x + nv[0] * gap;
        const ay = y + nv[1] * gap;
        let anchor = 'middle';
        if (nv[0] > 0.25) anchor = 'start';
        else if (nv[0] < -0.25) anchor = 'end';
        const x0 = anchor === 'start' ? ax : anchor === 'end' ? ax - lw : ax - lw / 2;
        const y0 = nv[1] > 0.35 ? ay : nv[1] < -0.35 ? ay - lh : ay - lh / 2;
        const rect = [x0, y0, x0 + lw, y0 + lh];
        const out = Math.max(0, 30 - x0) + Math.max(0, x0 + lw - 970) + Math.max(0, 30 - y0) + Math.max(0, y0 + lh - 970);
        const cost = placed.reduce((sum, b) => sum + overlap(rect, b), 0) + out * 400 + gap * 0.5;
        if (!best || cost < best.cost) best = { cost, rect, anchor, tx: anchor === 'start' ? x0 : anchor === 'end' ? x0 + lw : x0 + lw / 2 };
      });
      return best;
    };
    // Road names first, as faint region labels where each road leaves the map.
    const rfs = fs * 0.72;
    roads.forEach((road) => {
      if (!road.name) return;
      const label = road.name.toUpperCase();
      const lw = label.length * rfs * 0.98 + 4;
      const [ex, ey] = [road.end[0] - road.endT[0] * 26, road.end[1] - road.endT[1] * 26];
      const nv = [[-road.endT[1], road.endT[0]], [road.endT[1], -road.endT[0]]];
      const back = [-road.endT[0], -road.endT[1]];
      const best = pick(ex, ey, [[nv[0], 16], [nv[1], 16], [back, 20], [nv[0], 40], [nv[1], 40]], lw, rfs);
      placed.push(best.rect);
      const txt = rgsSvg('text', { x: fmt(best.tx), y: fmt(best.rect[1] + rfs * 0.82), 'text-anchor': best.anchor, 'font-size': fmt(rfs), class: 'rgs-tl-map__road-name' }, labelLayer);
      txt.textContent = label;
    });
    // Hub name under the hub, or above or beside it when a city is in the way.
    const hubName = (d.hubLabel || '').trim();
    const hubSub = (d.hubSub || '').trim();
    if (hubName || hubSub) {
      const hfs = fs * (compact ? 0.95 : 1.1);
      const sfs = fs * 0.68;
      const w = Math.max(hubName.length * hfs * 0.74, hubSub.length * sfs * 0.82) + 6;
      const h = (hubName ? hfs : 0) + (hubSub ? sfs + 8 : 0);
      const best = pick(C, C, [[[0, 1], 54], [[0, -1], 54], [[0, 1], 80], [[1, 0], 56], [[-1, 0], 56], [[0, -1], 80]], w, h);
      placed.push(best.rect);
      const tx = fmt(best.rect[0] + w / 2);
      const ht = rgsSvg('text', { x: tx, y: fmt(best.rect[1] + (hubName ? hfs : sfs) * 0.82), 'text-anchor': 'middle', class: 'rgs-tl-map__hub-label' }, labelLayer);
      if (hubName) { const a = rgsSvg('tspan', { 'font-size': fmt(hfs), class: 'rgs-tl-map__hub-name' }, ht); a.textContent = hubName.toUpperCase(); }
      if (hubSub) {
        const b = rgsSvg('tspan', { x: tx, dy: hubName ? fmt(sfs + 8) : 0, 'font-size': fmt(sfs), class: 'rgs-tl-map__hub-sub' }, ht);
        b.textContent = hubSub.toUpperCase();
      }
    }
    nodes.forEach(({ s, rad }) => {
      const [x, y] = s.p;
      const g = rgsSvg('g', { class: `rgs-tl-map__city${s.major ? ' is-major' : ''}`, transform: `translate(${fmt(x)} ${fmt(y)})` }, cityLayer);
      rgsSvg('circle', { r: rad, class: 'rgs-tl-map__ping' }, g);
      rgsSvg('circle', { r: rad, class: 'rgs-tl-map__dot' }, g);
      if (s.icon) {
        const ic = s.icon.cloneNode(true);
        const sz = rad * 1.15;
        ['x', 'y'].forEach((k) => ic.setAttribute(k, fmt(-sz / 2)));
        ['width', 'height'].forEach((k) => ic.setAttribute(k, fmt(sz)));
        ic.setAttribute('class', 'rgs-tl-map__icon');
        g.appendChild(ic);
      } else {
        rgsSvg('circle', { r: 5, class: 'rgs-tl-map__pip' }, g);
      }
      // Label: name and date, beside the node (outer side first).
      const name = clip(s.name || '', 18);
      const date = clip(s.date, 16).toUpperCase();
      const dfs = fs * 0.66;
      const lw = Math.max(name.length * fs * 0.58, date.length * dfs * 0.78) + 4;
      const lh = fs + (compact && !solo ? 0 : dfs + 6);
      const normals = [[-s.t[1], s.t[0]], [s.t[1], -s.t[0]]];
      const outward = x >= C ? 1 : -1;
      normals.sort((a, b) => b[0] * outward - a[0] * outward);
      const cands = [];
      [rad + 8, rad + 26, rad + 48].forEach((gap) => normals.forEach((nv) => cands.push([nv, gap])));
      [[outward, 0], [-outward, 0], [0, 1], [0, -1]].forEach((nv) => cands.push([nv, rad + 10]));
      const best = pick(x, y, cands, lw, lh);
      if (!solo) placed.push(best.rect);
      const { tx } = best;
      const label = rgsSvg('text', { x: fmt(tx), y: fmt(best.rect[1] + fs * 0.82), 'text-anchor': best.anchor, class: 'rgs-tl-map__label' }, labelLayer);
      const a = rgsSvg('tspan', { 'font-size': fmt(fs), class: 'rgs-tl-map__name' }, label);
      a.textContent = name;
      if (date) {
        const b = rgsSvg('tspan', { x: fmt(tx), dy: fmt(dfs + 6), 'font-size': fmt(dfs), class: 'rgs-tl-map__date' }, label);
        b.textContent = date;
      }
      cities[s.i] = { g, label, s };
    });
    view = { svg, roads, cities, hub };
  };

  const apply = (a) => {
    if (!view || a === cur) return;
    const prev = cur;
    cur = a;
    const { svg, roads, cities, hub } = view;
    roads.forEach((road) => {
      let len = 0;
      road.own.forEach((s) => { if (s.i <= a) len = s.len; });
      road.lit.forEach((p) => { p.style.strokeDashoffset = fmt(road.total + 1 - len); });
    });
    cities.forEach((c, i) => {
      if (!c) return;
      const on = i <= a;
      c.g.classList.toggle('is-lit', on);
      c.label.classList.toggle('is-lit', on);
      c.g.classList.toggle('is-active', i === a);
      c.label.classList.toggle('is-active', i === a);
    });
    const end = a >= 0 && stops[a].road === 'end';
    hub.classList.toggle('is-lit', a >= 0);
    hub.classList.toggle('is-active', a >= 0 && stops[a].road === 'hub');
    svg.classList.toggle('is-complete', end || (still && a === stops.length - 1));
    if (!still) items.forEach((it, i) => it.classList.toggle('is-active', i === a));
    svg.classList.toggle('is-back', a < prev);
  };

  tl.classList.add('rgs-tl--mapped');
  build();
  if (still) {
    tl.classList.add('rgs-tl--still');
    apply(stops.length - 1);
  }
  let raf = 0;
  const update = () => {
    raf = 0;
    if (!tl.isConnected) return;
    let line = innerHeight * 0.5;
    if (phone.matches) {
      // The map is pinned above the cards: read at the middle of the space left under it.
      const mb = box.getBoundingClientRect().bottom;
      line = Math.min(innerHeight * 0.85, Math.max(line, mb + (innerHeight - mb) * 0.4));
    }
    let a = -1;
    items.forEach((it, i) => { if (it.getBoundingClientRect().top <= line) a = i; });
    apply(a);
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
  const rebuild = () => {
    if (!tl.isConnected) return;
    const keep = cur;
    build();
    if (still) apply(stops.length - 1);
    else { cur = -2; tl.classList.add('rgs-tl--instant'); apply(keep); requestAnimationFrame(() => requestAnimationFrame(() => tl.classList.remove('rgs-tl--instant'))); }
  };
  phone.addEventListener('change', rebuild);
  if (still) return;
  document.addEventListener('scroll', queue, { capture: true, passive: true });
  addEventListener('resize', queue, { passive: true });
  update();
};

/* ---------------- Timeline road map (sections/rgs-timeline) ---------------- */
// Cards slide in as they scroll into view, and a solid line with a glowing tip draws down the dashed road,
// lighting each milestone it reaches (CSS: .rgs-tl--live). Without JS, with reduced motion or in the theme
// editor everything just shows. Horizon can scroll .page-wrapper instead of the window, so scroll events are
// caught on the document in the capture phase and positions come from getBoundingClientRect.
const rgsTimeline = (root = document) => {
  root.querySelectorAll('.rgs-tl:not([data-tl-ready])').forEach((tl) => {
    tl.dataset.tlReady = '';
    if (tl.classList.contains('rgs-tl--map')) { rgsTimelineMap(tl); return; }
    const road = tl.querySelector('.rgs-tl__road');
    const items = [...tl.querySelectorAll('.rgs-tl__item')];
    const nodes = items.map((it) => it.querySelector('.rgs-tl__node'));
    if (!road || !items.length || nodes.includes(null) || reduceMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
    tl.classList.add('rgs-tl--live');
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    }), { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });
    items.forEach((it) => io.observe(it));
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = road.getBoundingClientRect();
      const mid = (n) => { const b = n.getBoundingClientRect(); return b.top + b.height / 2 - r.top; };
      const first = nodes[0].getBoundingClientRect();
      const top = mid(nodes[0]);
      const end = mid(nodes[nodes.length - 1]);
      // The line follows a point 60% of the way down the screen.
      const reach = Math.min(end, innerHeight * 0.6 - r.top);
      road.style.setProperty('--tl-px', `${first.left + first.width / 2 - r.left}px`);
      road.style.setProperty('--tl-pt', `${top}px`);
      road.style.setProperty('--tl-ph', `${Math.max(0, reach - top)}px`);
      road.style.setProperty('--tl-tip', reach > top && reach < end ? '1' : '0');
      nodes.forEach((n, i) => { if (mid(n) <= reach + 1) items[i].classList.add('is-lit'); });
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
    document.addEventListener('scroll', queue, { capture: true, passive: true });
    addEventListener('resize', queue, { passive: true });
    update();
  });
};
rgsTimeline();
document.addEventListener('shopify:section:load', (e) => rgsTimeline(e.target));
